/* eslint-disable react/prop-types */
"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import api from "../../api/axios";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import useChatAttachments from "../../components/menu/hooks/useChatAttachments";
import PhotoRequestModal from "../../components/events/PhotoRequestModal";
import {
  CHAT_MAX_FILE_MB,
  CHAT_MAX_FILE_BYTES,
} from "../../constants/chatUploads";
import { normalizeMessageReactions } from "../../utils/chatReactions";
import { applyChatPinnedMessages, getChatPinnedMessages, removeChatPinnedMessage } from "../../utils/chatPinnedMessages";
import { DirectChatMobileView } from "../../components/menu/ChatMobileView";
import { DirectChatDesktopView } from "../../components/menu/ChatDesktopView";
import ChatInputBar from "../../components/menu/ChatInputBar";
import ClubChatNotificationsModal from "../../components/menu/ClubChatNotificationsModal";
import DirectChatPhotoConsentModal from "../../components/menu/DirectChatPhotoConsentModal";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import useDirectChatVisualViewport from "../../hooks/useDirectChatVisualViewport";
import { buildWsUrl, toApiAbsoluteUrl } from "../../utils/ws";
import useChatZoomLock from "../../hooks/useChatZoomLock";
import useChatDraftPersistence from "../../hooks/useChatDraftPersistence";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
  isDeletedUsername,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { resolveChatOpenReadTarget } from "../../utils/chatReadState";
import {
  reconcileChatReadPosition,
  rememberChatRead,
} from "../../utils/chatReadPosition";
import {
  formatDirectLastSeenDate,
  getDirectPresenceExpiryDelay,
  resolveDirectPeerPresenceUpdate,
} from "../../utils/directChatLastSeen";
import {
  captureChatPrependPosition,
  findOldestChatItemId,
  prependOlderChatPage,
  restoreChatPrependPosition,
} from "../../utils/chatPrependScroll";
import { resolveFirstUnreadMessageId } from "../../utils/chatUnreadFocus";
import { scheduleChatFeedScrollToBottom } from "../../utils/chatAutoScroll";
import { loadChatMessagePage } from "../../utils/chatMessageRequest";
import { sendFirstDirectChatMessage } from "../../utils/directChatFirstMessage";
import { stabilizeChatInitialPosition } from "../../utils/chatInitialPosition";
import {
  buildOptimisticChatMessage,
  findMatchingOptimisticClientId,
  reconcileOptimisticChatMessage,
  removeOptimisticChatMessage,
} from "../../utils/chatOptimisticMessages";
import { isChatPollPayload } from "../../utils/chatStructuredPayloads";
import { shouldClosePhotoConsentModalForChatChange } from "../../utils/directChatPhotoConsent";
import {
  isInboxEventForOpenChat,
  mergeLatestChatPage,
} from "../../utils/chatRealtimeSync";
import { getChatSticker } from "../../constants/chatStickers";
import { isNativeApp } from "../../native/runtime";

const ITEM_TYPES = {
  MESSAGE: "message",
  SYSTEM: "system",
  DAY: "day_separator",
};

const PAGE_LIMIT = 50;

const EMPTY_PHOTO_SHARING_STATE = Object.freeze({
  allowed: false,
  accepted_request_id: null,
  my_request: null,
  incoming_request: null,
  can_request: true,
  can_respond: false,
});

function normalizePhotoSharingState(raw) {
  if (!raw || typeof raw !== "object") return { ...EMPTY_PHOTO_SHARING_STATE };
  return {
    allowed: Boolean(raw?.allowed),
    accepted_request_id: raw?.accepted_request_id ?? null,
    my_request: raw?.my_request && typeof raw.my_request === "object" ? raw.my_request : null,
    incoming_request:
      raw?.incoming_request && typeof raw.incoming_request === "object"
        ? raw.incoming_request
        : null,
    can_request: Boolean(raw?.can_request),
    can_respond: Boolean(raw?.can_respond),
  };
}

function mergePhotoSharingPayload(previous, payload, viewerUserId) {
  if (String(payload?.kind || "").trim().toLowerCase() !== "direct_photo_consent") {
    return previous;
  }
  const request = {
    id: Number(payload?.request_id || 0) || null,
    status: String(payload?.status || "pending"),
    requested_by_id: Number(payload?.requested_by_id || 0) || null,
    responded_by_id: Number(payload?.responded_by_id || 0) || null,
    requested_at: payload?.requested_at || null,
    responded_at: payload?.responded_at || null,
  };
  const isOwn = Number(request.requested_by_id || 0) === Number(viewerUserId || 0);
  const allowed = request.status === "accepted" || Boolean(previous?.allowed);
  return {
    ...normalizePhotoSharingState(previous),
    allowed,
    accepted_request_id: request.status === "accepted" ? request.id : previous?.accepted_request_id || null,
    my_request: isOwn ? request : previous?.my_request || null,
    incoming_request: isOwn ? previous?.incoming_request || null : request,
    can_request: false,
    can_respond: !allowed && !isOwn && ["pending", "declined"].includes(request.status),
  };
}

function formatShortTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return "";
  }
}

function getPeerLastSeenValue(peer) {
  if (!peer || typeof peer !== "object") return null;
  return (
    peer.last_seen_at ||
    peer.lastSeenAt ||
    peer.last_activity_at ||
    peer.lastActivityAt ||
    peer.previous_seen_at ||
    null
  );
}

function formatDirectLastSeenLabel(peer, t, locale) {
  if (peer?.is_online || peer?.isOnline) {
    return t("chats.direct_chat.last_seen_online", {
      defaultValue: "Online",
    });
  }

  const raw = getPeerLastSeenValue(peer);
  if (!raw) {
    return t("chats.direct_chat.last_seen_unknown", {
      defaultValue: "Last seen recently",
    });
  }

  const dt = new Date(raw);
  const timeMs = dt.getTime();
  if (!Number.isFinite(timeMs)) {
    return t("chats.direct_chat.last_seen_unknown", {
      defaultValue: "Last seen recently",
    });
  }

  const diffMs = Math.max(0, Date.now() - timeMs);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < 2 * minuteMs) {
    return t("chats.direct_chat.last_seen_just_now", {
      defaultValue: "Last seen just now",
    });
  }
  if (diffMs < hourMs) {
    return t("chats.direct_chat.last_seen_minutes_ago", {
      count: Math.max(1, Math.floor(diffMs / minuteMs)),
      defaultValue: "Last seen: {{count}} min ago",
    });
  }
  if (diffMs < dayMs) {
    return t("chats.direct_chat.last_seen_hours_ago", {
      count: Math.max(1, Math.floor(diffMs / hourMs)),
      defaultValue: "Last seen: {{count}} h ago",
    });
  }
  if (diffMs < 7 * dayMs) {
    const count = Math.max(1, Math.floor(diffMs / dayMs));
    if (count === 1) {
      return t("chats.direct_chat.last_seen_day_ago", {
        defaultValue: "Last seen: 1 day ago",
      });
    }
    return t("chats.direct_chat.last_seen_days_ago", {
      count,
      defaultValue: "Last seen: {{count}} days ago",
    });
  }

  let dateLabel = "";
  try {
    dateLabel = formatDirectLastSeenDate(dt, locale);
  } catch {
    dateLabel = dt.toLocaleDateString();
  }

  return t("chats.direct_chat.last_seen_date", {
    date: dateLabel,
    defaultValue: "Last seen: {{date}}",
  });
}

function normalizeWsAttachments(rawAtt) {
  const list = Array.isArray(rawAtt) ? rawAtt : [];
  return list
    .map((a) => {
      const type = a?.type || a?.attachment_type || null;
      const rawUrl = a?.url || a?.file_url || a?.file || a?.external_url || null;
      const url = rawUrl ? toApiAbsoluteUrl(rawUrl) : null;
      if (!type || !url) return null;
      return {
        type,
        url,
        width: a?.width ?? null,
        height: a?.height ?? null,
        original_name: a?.original_name ?? null,
        size_bytes: a?.size_bytes ?? null,
      };
    })
    .filter(Boolean);
}

function normalizeDirectEventContext(raw) {
  if (!raw || typeof raw !== "object") return null;
  const eventId = Number(raw?.eventId || raw?.event_id || raw?.id || 0);
  if (!eventId) return null;

  return {
    id: eventId,
    name: String(raw?.name || "").trim(),
    type: String(raw?.type || "event").trim() || "event",
    status: String(raw?.status || "active").trim() || "active",
    date_time: raw?.date_time || raw?.dateTime || null,
    country: raw?.country || null,
    city: raw?.city || null,
    event_format: raw?.event_format ?? raw?.eventFormat ?? null,
    host_format: raw?.host_format ?? raw?.hostFormat ?? null,
    badge_label: raw?.badge_label || raw?.badgeLabel || null,
    icon_image_url: toApiAbsoluteUrl(raw?.icon_image_url || raw?.iconImageUrl),
    pricing_model: String(raw?.pricing_model || raw?.pricingModel || "free").trim() || "free",
    join_by_request: Boolean(raw?.join_by_request ?? raw?.joinByRequest),
    is_verified: Boolean(raw?.is_verified ?? raw?.isVerified),
    is_validated:
      raw?.is_validated === false || raw?.isValidated === false ? false : true,
    public_path: String(raw?.public_path || raw?.publicPath || "").trim() || null,
  };
}

function hydrateDirectEventContextPayload(
  rawMessage,
  eventContext,
  eventContextId,
  eventContextMessageText
) {
  const payload =
    rawMessage?.payload && typeof rawMessage.payload === "object"
      ? rawMessage.payload
      : null;
  const payloadKind = String(payload?.kind || "").trim().toLowerCase();
  if (payloadKind === "event_host_contact_context") {
    const payloadEventId = Number(payload?.event_id || payload?.event?.id || 0);
    if (eventContext && payloadEventId === Number(eventContextId || 0)) {
      return {
        ...payload,
        event: {
          ...(payload.event || {}),
          icon_image_url: eventContext.icon_image_url,
        },
      };
    }
    return payload;
  }

  if (!eventContext || Number(eventContextId || 0) <= 0) {
    return payload;
  }

  const messageType = String(rawMessage?.message_type || "")
    .trim()
    .toLowerCase();
  const messageText = String(rawMessage?.text || "").trim();
  const expectedText = String(eventContextMessageText || "").trim();

  if (
    messageType !== "system_as_user" ||
    !expectedText ||
    messageText !== expectedText
  ) {
    return payload;
  }

  return {
    ...(payload || {}),
    kind: "event_host_contact_context",
    event_id: Number(eventContextId),
    event: eventContext,
  };
}

function insertItemSortedById(items, nextItem) {
  const list = Array.isArray(items) ? items : [];
  const nextId = Number(nextItem?.id || 0);
  if (!nextId) return [...list, nextItem];

  const existingIdx = list.findIndex((item) => Number(item?.id || 0) === nextId);
  if (existingIdx >= 0) {
    const next = [...list];
    next[existingIdx] = { ...next[existingIdx], ...nextItem };
    return next;
  }

  const insertAt = list.findIndex((item) => {
    const currentId = Number(item?.id || 0);
    return currentId > 0 && currentId > nextId;
  });

  if (insertAt < 0) {
    return [...list, nextItem];
  }

  return [...list.slice(0, insertAt), nextItem, ...list.slice(insertAt)];
}

export default function DirectChat({
  chat,
  onBack,
  onInitialReady = null,
  onInitialError = null,
  deferInputUntilReady = false,
}) {
  const { t, i18n } = useTranslation("menu");
  const { t: tv } = useTranslation("vault");
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  useChatZoomLock(true);
  const focusFirstUnreadFromPush = location?.state?.focusFirstUnread === true;
  const pushUnreadFocusKey = focusFirstUnreadFromPush
    ? [
        location?.key || "default",
        location?.state?.pushNotificationId || "",
        location?.state?.pushMessageId || "",
      ].join(":")
    : "";

  // ---- Route / props fallbacks ----
  // URL can be either a numeric direct chat id OR a 3-char code like /menu/chats/direct/VGD
  const rawParamId = params?.directChatId || params?.chatId || params?.id || null;

  // chat from list (if we navigated from chats list)
  const directChatIdFromChat = chat?.direct_chat_id || chat?.chat_id || chat?.id || null;

  // We expect the list screen to pass peer info via `state`, but we also accept it via `chat`.
  const peer =
    chat?.peer ||
    chat?.peer_user ||
    chat?.user ||
    chat?.other_user ||
    location?.state?.peer ||
    location?.state?.user ||
    null;
  const peerUserId = Number(peer?.id || 0);
  const peerOnlineFromPayload = Boolean(peer?.is_online || peer?.isOnline);
  const peerLastSeenFromPayload = getPeerLastSeenValue(peer);
  const peerPresenceExpiryFromPayload = peer?.presence_expires_at || null;
  const peerUserIdRef = useRef(peerUserId);
  peerUserIdRef.current = peerUserId;
  const [peerPresence, setPeerPresence] = useState(() => ({
    is_online: peerOnlineFromPayload,
    last_seen_at: peerLastSeenFromPayload,
    presence_expires_at: peerPresenceExpiryFromPayload,
  }));
  const [, setLastSeenClock] = useState(() => Date.now());
  const visiblePeer = peer
    ? {
        ...peer,
        is_online: peerPresence.is_online,
        last_seen_at: peerPresence.last_seen_at,
      }
    : peer;
  const isDeletedPeer = isDeletedAccountUser(visiblePeer);

  useEffect(() => {
    setPeerPresence({
      is_online: peerOnlineFromPayload,
      last_seen_at: peerLastSeenFromPayload,
      presence_expires_at: peerPresenceExpiryFromPayload,
    });
  }, [
    peerUserId,
    peerOnlineFromPayload,
    peerLastSeenFromPayload,
    peerPresenceExpiryFromPayload,
  ]);

  useEffect(() => {
    if (peerPresence.is_online) return undefined;
    const timerId = window.setInterval(() => setLastSeenClock(Date.now()), 60_000);
    return () => window.clearInterval(timerId);
  }, [peerPresence.is_online]);

  useEffect(() => {
    if (!peerPresence.is_online || !peerPresence.presence_expires_at) return undefined;
    const expectedExpiry = peerPresence.presence_expires_at;
    const delay = getDirectPresenceExpiryDelay(expectedExpiry);
    if (delay == null) return undefined;

    const timerId = window.setTimeout(() => {
      setPeerPresence((current) => {
        if (current.presence_expires_at !== expectedExpiry) return current;
        return {
          ...current,
          is_online: false,
          presence_expires_at: null,
        };
      });
    }, delay);
    return () => window.clearTimeout(timerId);
  }, [peerPresence.is_online, peerPresence.presence_expires_at]);
  const eventContext = React.useMemo(
    () =>
      normalizeDirectEventContext(
        chat?.eventContext || location?.state?.eventContext || null
      ),
    [chat?.eventContext, location?.state?.eventContext]
  );
  const eventContextId = Number(eventContext?.id || 0);

  // Resolved numeric direct-chat id (what backend expects)
  const [resolvedDirectChatId, setResolvedDirectChatId] = useState(() => {
    const candidate = directChatIdFromChat ?? rawParamId;
    if (candidate == null) return null;
    const s = String(candidate);
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  });

  // Direct peer code (preferred from peer, fallback to URL param if it looks like 3 chars)
  const directCode = (() => {
    if (isDeletedPeer) return null;
    if (visiblePeer?.code) return String(visiblePeer.code).trim().toUpperCase();
    if (typeof rawParamId === "string" && /^[A-Za-z0-9]{3}$/.test(rawParamId)) {
      return rawParamId.toUpperCase();
    }
    return null;
  })();
  const directLastSeenLabel = isDeletedPeer
    ? ""
    : formatDirectLastSeenLabel(
        visiblePeer,
        t,
        i18n?.resolvedLanguage || i18n?.language
      );

  // Sync: if numeric id appears/changes from router or parent
  useEffect(() => {
    const candidate = directChatIdFromChat ?? rawParamId;
    if (candidate == null) return;
    const s = String(candidate);
    if (!/^\d+$/.test(s)) return;
    const n = Number(s);
    if (!Number.isFinite(n)) return;
    if (n === resolvedDirectChatId) return;
    setResolvedDirectChatId(n);
  }, [directChatIdFromChat, rawParamId, resolvedDirectChatId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/accounts/profile/");
        if (cancelled) return;
        const uid = Number(resp?.data?.id || 0);
        setCurrentUserId(Number.isFinite(uid) && uid > 0 ? uid : null);
        setCurrentUserIsSuperuser(Boolean(resp?.data?.is_superuser));
        setHasCustomAvatar(Boolean(resp?.data?.avatar_custom_url));
      } catch {
        if (!cancelled) {
          setCurrentUserId(null);
          setCurrentUserIsSuperuser(false);
          setHasCustomAvatar(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // NOTE: DM endpoints use /chats/direct/<id>/... on backend.
  const messagesBaseUrl = resolvedDirectChatId
    ? `/chats/direct/${encodeURIComponent(String(resolvedDirectChatId))}`
    : null;

  const [items, setItems] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const pendingJumpTargetRef = useRef(null);
  const pendingJumpAttemptsRef = useRef(0);
  const loadOlderMessagesRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserIsSuperuser, setCurrentUserIsSuperuser] = useState(false);
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const [typingUsers, setTypingUsers] = useState([]);
  const typingTimersRef = useRef({});
  const chatWsRef = useRef(null);
  const wsReconnectTimerRef = useRef(null);
  const wsReconnectAttemptRef = useRef(0);
  const latestSyncTimerRef = useRef(null);
  const [latestSyncVersion, setLatestSyncVersion] = useState(0);
  const typingStateRef = useRef(false);
  const typingStopTimerRef = useRef(null);
  const typingLastSentRef = useRef(0);
  const requestLatestSync = useCallback(() => {
    if (latestSyncTimerRef.current) return;
    latestSyncTimerRef.current = window.setTimeout(() => {
      latestSyncTimerRef.current = null;
      setLatestSyncVersion((version) => version + 1);
    }, 0);
  }, []);

  useEffect(() => () => {
    if (latestSyncTimerRef.current) {
      window.clearTimeout(latestSyncTimerRef.current);
      latestSyncTimerRef.current = null;
    }
  }, []);

  // ===== Unread/read meta (MVP) =====
  const [chatMeta, setChatMeta] = useState({
    last_read_message_id: 0,
    peer_last_read_message_id: 0,
    unread_count: 0,
    first_unread_message_id: 0,
    last_message_id: 0,
    notifications_muted: Boolean(chat?.notifications_muted),
  });
  const peerLastReadRef = useRef(0);

  // One-time initial positioning when opening the chat
  const didInitialScrollRef = useRef(false);
  const initialPositionCleanupRef = useRef(null);
  const initialReadyNotifiedRef = useRef(false);
  const [initialMessagesSettled, setInitialMessagesSettled] = useState(
    () => !messagesBaseUrl
  );
  const [initialPositionSettled, setInitialPositionSettled] = useState(
    () => !messagesBaseUrl
  );

  // Reset initial-position guard on chat switch/reopen
  useEffect(() => {
    initialPositionCleanupRef.current?.();
    initialPositionCleanupRef.current = null;
    didInitialScrollRef.current = false;
    initialReadyNotifiedRef.current = false;
    setInitialMessagesSettled(!messagesBaseUrl);
    setInitialPositionSettled(!messagesBaseUrl);
  }, [messagesBaseUrl, pushUnreadFocusKey]);

  useEffect(
    () => () => {
      initialPositionCleanupRef.current?.();
      initialPositionCleanupRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (
      initialReadyNotifiedRef.current ||
      !initialMessagesSettled ||
      !initialPositionSettled
    ) {
      return;
    }
    initialReadyNotifiedRef.current = true;
    onInitialReady?.();
  }, [initialMessagesSettled, initialPositionSettled, onInitialReady]);

  useEffect(() => {
    setReportedInlineMessageIds(new Set());
  }, [messagesBaseUrl]);

  // Throttle/RAF helpers for scroll-based read marking
  const rafScrollRef = useRef(null);
  const readUpToTimerRef = useRef(null);
  const lastSentReadUpToRef = useRef(0);

  // Keep latest values for unmount cleanup
  const lastMessageIdRef = useRef(0);
  const messagesBaseUrlRef = useRef(null);
  const latestLoadedBaseUrlRef = useRef(null);

  useEffect(() => {
    if (readUpToTimerRef.current) {
      window.clearTimeout(readUpToTimerRef.current);
      readUpToTimerRef.current = null;
    }
    lastSentReadUpToRef.current = 0;
    lastMessageIdRef.current = 0;
  }, [messagesBaseUrl]);

  // Used to allow auto-scroll to bottom after I send a message
  const justSentRef = useRef(false);
  const sendInFlightRef = useRef(false);
  const isPrependingRef = useRef(false);
  // When the first DM message creates a chat, preserve optimistic items if initial GET races behind.
  const preserveOptimisticOnEmptyLoadRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [preStartQuote, setPreStartQuote] = useState(null);
  const [hasCustomAvatar, setHasCustomAvatar] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoGateTarget, setPhotoGateTarget] = useState(null);
  const [photoSharingState, setPhotoSharingState] = useState(() => ({
    ...EMPTY_PHOTO_SHARING_STATE,
  }));
  const [photoConsentModalOpen, setPhotoConsentModalOpen] = useState(false);
  const [photoConsentSubmitting, setPhotoConsentSubmitting] = useState(false);
  const [photoSourceMenuRequestId, setPhotoSourceMenuRequestId] = useState(0);
  const [localEventContextActivated, setLocalEventContextActivated] = useState(false);
  const photoConsentChatUrlRef = useRef(messagesBaseUrl);

  // ===== Attachments (images only) MVP =====
  const {
    fileInputRef,
    pendingImages,
    onPickImages,
    openNativeImagePicker,
    removePendingImage,
    clearPendingImages,
    MAX_ATTACHMENTS,
  } = useChatAttachments({ allowAttachments: true });

  const feedRef = useRef(null);
  const feedContentRef = useRef(null);
  const inputRef = useRef(null);
  const headerRef = useRef(null);
  const inputBarWrapRef = useRef(null);
  const [pinSparseMobileFeed, setPinSparseMobileFeed] = useState(false);

  const isDesktop = useIsDesktop();
  const directChatViewportStyle = useDirectChatVisualViewport(isDesktop, feedRef);

  useEffect(() => {
    const previousMessagesBaseUrl = photoConsentChatUrlRef.current;
    photoConsentChatUrlRef.current = messagesBaseUrl;
    setPhotoSharingState({ ...EMPTY_PHOTO_SHARING_STATE });
    if (
      shouldClosePhotoConsentModalForChatChange(
        previousMessagesBaseUrl,
        messagesBaseUrl
      )
    ) {
      setPhotoConsentModalOpen(false);
    }
  }, [messagesBaseUrl]);

  const openPhotoModal = useCallback((nextAction = null) => {
    setPhotoGateTarget(nextAction);
    setIsPhotoModalOpen(true);
  }, []);

  const checkProfilePhoto = useCallback(async (nextAction = null) => {
    if (hasCustomAvatar === true) {
      return true;
    }

    if (hasCustomAvatar === false) {
      openPhotoModal(nextAction);
      return false;
    }

    try {
      const { data } = await api.get("/accounts/profile/");
      const avatarUrl = data?.avatar_custom_url || null;
      if (avatarUrl) {
        setHasCustomAvatar(true);
        return true;
      }

      setHasCustomAvatar(false);
      openPhotoModal(nextAction);
      return false;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        tv("main.avatar_required_for_feature");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status },
        })
      );
      return false;
    }
  }, [hasCustomAvatar, openPhotoModal, tv]);

  // ===== Dynamic bottom padding so the last message is NEVER hidden behind BottomNavigation + ChatInputBar =====
  const [feedBottomPadPx, setFeedBottomPadPx] = useState(16);
  const lastFeedBottomPadRef = useRef(16);

  const getBottomNavHeightPx = () => {
    try {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--bottom-nav-height")
        .trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const getHeaderBottomPx = () => {
    try {
      const r = headerRef.current?.getBoundingClientRect?.();
      return r?.bottom ?? 0;
    } catch {
      return 0;
    }
  };

  const getInputBarHeightPx = () => {
    try {
      const r = inputBarWrapRef.current?.getBoundingClientRect?.();
      return r?.height ?? 0;
    } catch {
      return 0;
    }
  };

  const recomputeFeedBottomPadding = () => {
    const feed = feedRef.current;
    if (!feed) return;

    const MIN_PAD = 16;
    const GAP = 8;

    const vv = window.visualViewport;
    const viewportTop = vv && Number.isFinite(vv.offsetTop) ? vv.offsetTop : 0;
    const viewportHeight = vv && Number.isFinite(vv.height) ? vv.height : window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const MAX_PAD = Math.max(MIN_PAD, Math.ceil(viewportHeight * 0.75));

    let pad = MIN_PAD;

    try {
      const feedRect = feed.getBoundingClientRect();
      const feedBottom = Math.min(
        Number.isFinite(feedRect.bottom) ? feedRect.bottom : viewportBottom,
        viewportBottom
      );

      const inputRect = inputBarWrapRef.current?.getBoundingClientRect?.();
      const inputTop = inputRect && Number.isFinite(inputRect.top) ? inputRect.top : null;
      const inputH = inputRect && Number.isFinite(inputRect.height) ? inputRect.height : 0;

      let coverTop = null;

      if (inputTop != null && inputH > 0) {
        coverTop = inputTop;
      } else {
        const nav = document.getElementById("bottom-nav");
        const navRect = nav?.getBoundingClientRect?.();
        if (navRect && Number.isFinite(navRect.top)) coverTop = navRect.top;
      }

      if (
        coverTop == null ||
        !Number.isFinite(coverTop) ||
        coverTop < viewportTop - 8 ||
        coverTop > viewportBottom + 8
      ) {
        coverTop = null;
      }

      if (coverTop != null) {
        const overlap = Math.max(0, feedBottom - coverTop);
        pad = Math.max(MIN_PAD, Math.ceil(overlap + GAP));
      } else {
        const approxNav = Math.max(0, Math.min(getBottomNavHeightPx(), viewportHeight));
        const approxInput = Math.max(0, Math.min(getInputBarHeightPx(), viewportHeight));
        const approx = approxNav + approxInput + GAP;
        pad = Math.max(MIN_PAD, Math.ceil(approx));
      }

      pad = Math.min(pad, MAX_PAD);

      const prev = lastFeedBottomPadRef.current || MIN_PAD;
      if (pad > prev * 3 && pad > MIN_PAD + 24) {
        pad = Math.min(prev + 48, MAX_PAD);
      }
    } catch {
      pad = lastFeedBottomPadRef.current || MIN_PAD;
    }

    if (Math.abs((lastFeedBottomPadRef.current || 0) - pad) >= 2) {
      lastFeedBottomPadRef.current = pad;
      setFeedBottomPadPx(pad);
    }
  };

  // ===== Message actions (context menu) =====
  const [ctxMenu, setCtxMenu] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const directDraftIdentity = resolvedDirectChatId
    ? `direct:id:${resolvedDirectChatId}`
    : directCode
      ? `direct:code:${directCode}`
      : peer?.id
        ? `direct:peer:${peer.id}`
        : null;
  const {
    clearDraft: clearChatDraft,
    persistDraft: persistChatDraft,
    restoreDraft: restoreChatDraft,
  } = useChatDraftPersistence({
    userId: currentUserId,
    chatIdentity: directDraftIdentity,
    value: inputValue,
    setValue: setInputValue,
    inputRef,
    disabled: Boolean(editingId || sending),
  });
  const [editingMeta, setEditingMeta] = useState(null);
  const [flashId, setFlashId] = useState(null);

  // ===== Image viewer overlay (Telegram-like) =====
  const [imageViewer, setImageViewer] = useState(null);

  // ===== Block / report modal (DM) =====
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [reportCategory, setReportCategory] = useState("");
  const [reportComment, setReportComment] = useState("");
  const [blockSubmitting, setBlockSubmitting] = useState(false);
  const [reportModalMode, setReportModalMode] = useState("user");
  const [reportMessageTargetId, setReportMessageTargetId] = useState(null);
  const [reportedInlineMessageIds, setReportedInlineMessageIds] = useState(() => new Set());
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [chatNotificationsModalOpen, setChatNotificationsModalOpen] = useState(false);
  const [chatNotificationsSubmitting, setChatNotificationsSubmitting] = useState(false);
  const chatNotificationsMuted = Boolean(chatMeta?.notifications_muted);
  const canToggleChatNotifications = Boolean(resolvedDirectChatId);

  const openChatNotificationsModal = useCallback(() => {
    if (!canToggleChatNotifications) return;
    setChatNotificationsModalOpen(true);
  }, [canToggleChatNotifications]);

  const closeChatNotificationsModal = useCallback(() => {
    if (chatNotificationsSubmitting) return;
    setChatNotificationsModalOpen(false);
  }, [chatNotificationsSubmitting]);

  const submitChatNotificationsToggle = useCallback(async () => {
    if (!resolvedDirectChatId || chatNotificationsSubmitting) return;
    const nextMuted = !chatNotificationsMuted;
    setChatNotificationsSubmitting(true);
    try {
      const response = await api.post(
        `/chats/internal/${encodeURIComponent(String(resolvedDirectChatId))}/notifications/`,
        { muted: nextMuted }
      );
      const confirmedMuted = Boolean(response?.data?.notifications_muted);
      setChatMeta((prev) => ({ ...prev, notifications_muted: confirmedMuted }));
      setChatNotificationsModalOpen(false);
      window.dispatchEvent(new CustomEvent("vg:chat-inbox-updated"));
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: confirmedMuted
              ? t("chats.notifications.disabled_toast")
              : t("chats.notifications.enabled_toast"),
          },
        })
      );
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message:
              error?.response?.data?.error || t("chats.notifications.update_failed"),
          },
        })
      );
    } finally {
      setChatNotificationsSubmitting(false);
    }
  }, [
    chatNotificationsMuted,
    chatNotificationsSubmitting,
    resolvedDirectChatId,
    t,
  ]);

  const submitPhotoConsentAction = useCallback(
    async (action, requestId = null, { openPickerAfterAccept = false } = {}) => {
      if (photoConsentSubmitting) return;
      setPhotoConsentSubmitting(true);
      try {
        const chatId = Number(resolvedDirectChatId || 0);
        let response;
        if (chatId > 0) {
          response = await api.post(
            `/chats/direct/${encodeURIComponent(String(chatId))}/photo-consent/`,
            { action, ...(requestId ? { request_id: requestId } : {}) },
            { suppressGlobalError: true }
          );
        } else if (action === "request") {
          const payload = { action: "request", start_photo_consent: "request" };
          if (peer?.id) payload.peer_id = peer.id;
          else if (directCode) payload.code = directCode;
          else throw new Error("direct_chat_peer_missing");
          if (eventContextId > 0) payload.event_id = eventContextId;

          response = await api.post("/chats/direct/open-or-create/", payload, {
            suppressGlobalError: true,
          });
          const nextId = Number(response?.data?.direct_chat_id || response?.data?.chat_id || 0);
          if (!Number.isFinite(nextId) || nextId <= 0 || !response?.data?.message) {
            throw new Error("direct_chat_photo_consent_message_missing");
          }
          messagesBaseUrlRef.current = `/chats/direct/${encodeURIComponent(String(nextId))}`;
          preserveOptimisticOnEmptyLoadRef.current = messagesBaseUrlRef.current;
          setResolvedDirectChatId(nextId);
        } else {
          throw new Error("direct_chat_photo_consent_chat_missing");
        }
        const nextState = normalizePhotoSharingState(response?.data?.photo_sharing);
        setPhotoSharingState(nextState);
        const responseMessage = response?.data?.message;
        const responseMessageId = Number(responseMessage?.id || 0);
        if (responseMessageId > 0) {
          const responseAuthorId = Number(responseMessage?.author_id || 0);
          const isSelf =
            currentUserIdRef.current != null &&
            responseAuthorId === Number(currentUserIdRef.current);
          const responseItem = {
            type: ITEM_TYPES.MESSAGE,
            created_at: responseMessage?.created_at || null,
            id: responseMessageId,
            author_id: responseAuthorId || null,
            message_type: responseMessage?.message_type || "system_as_user",
            payload: responseMessage?.payload || null,
            text: responseMessage?.text || "",
            time: formatShortTime(responseMessage?.created_at),
            role: responseMessage?.author_role || null,
            author_is_deleted_account: !!responseMessage?.author_is_deleted_account,
            is_self: isSelf,
            sender_username: responseMessage?.author_username || "@user",
            sender_code: responseMessage?.author_code || null,
            edited_at: responseMessage?.edited_at || null,
            reply_to_id: null,
            reply_to: null,
            attachments: normalizeWsAttachments(responseMessage?.attachments),
            reactions: normalizeMessageReactions(
              responseMessage?.reactions,
              currentUserIdRef.current
            ),
            read_by_peer: false,
          };
          setItems((prev) => insertItemSortedById(prev, responseItem));
        }
        setPhotoConsentModalOpen(false);

        if (action === "request") {
          window.dispatchEvent(
            new CustomEvent("global-info", {
              detail: { message: t("chats.direct_chat.photo_consent.request_sent") },
            })
          );
        }
        if (action === "accept" && openPickerAfterAccept && nextState.allowed) {
          if (isNativeApp()) {
            setPhotoSourceMenuRequestId((value) => value + 1);
          } else {
            window.setTimeout(() => fileInputRef.current?.click?.(), 0);
          }
        }
      } catch (error) {
        const responseState = error?.response?.data?.photo_sharing;
        if (responseState) setPhotoSharingState(normalizePhotoSharingState(responseState));
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              message:
                error?.response?.data?.error ||
                t("chats.direct_chat.photo_consent.update_failed"),
              status: error?.response?.status,
            },
          })
        );
      } finally {
        setPhotoConsentSubmitting(false);
      }
    },
    [
      directCode,
      eventContextId,
      fileInputRef,
      peer?.id,
      photoConsentSubmitting,
      resolvedDirectChatId,
      t,
    ]
  );

  // ===== ChatInputBar is rendered inside BottomNavigation (slot) =====
  const chatInputSlotOpenedRef = useRef(false);
  const slotKeyRef = useRef(null);

  const closeChatInputSlot = () => {
    try {
      window.dispatchEvent(new CustomEvent("vg-chat-input-close"));
    } catch {
      // ignore
    }
  };

  const setBottomNavSuppressed = (active) => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-bottom-nav-suppression", {
          detail: {
            active: !!active,
            source: "direct-chat-overlay",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  const openChatInputSlotOnce = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-chat-input-open", {
          detail: {
            props: getChatInputSlotProps(),
            no_auto_expand: true,
            source: "direct-chat",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  const updateChatInputSlotProps = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-chat-input-update", {
          detail: {
            props: getChatInputSlotProps(),
            no_auto_expand: true,
            source: "direct-chat",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  const getChatInputSlotProps = () => ({
    t,

    // refs
    inputRef,
    fileInputRef,
    wrapRef: inputBarWrapRef,

    // state
    inputValue,
    sending,
    replyTo,
    editingId,
    editingMeta,
    pendingImages,
    unreadCount: chatMeta?.unread_count || 0,
    typingUsers,
    showTypingIndicator: false,
    overlayDimmed: !!ctxMenu?.item,

    // capabilities
    allowAttachments: true,
    attachmentsUnlocked: Boolean(photoSharingState?.allowed),
    photoOnlyAttachments: true,

    // handlers
    onInputChange: handleInputChange,
    onInputKeyDown: handleInputKeyDown,
    onSend: handleSend,
    onSendSticker: (stickerId) => handleSend({ stickerId }),
    onPickImages,
    onOpenNativeImagePicker: openNativeImagePicker,
    photoSourceMenuRequestId,
    onLockedAttachmentsClick: () => setPhotoConsentModalOpen(true),
    onRemoveImage: removePendingImage,
    onCancelContext: () => {
      const wasEditing = !!editingId;
      setReplyTo(null);
      setEditingId(null);
      setEditingMeta(null);
      if (wasEditing) setInputValue("");
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
    },
    onOverlayDismiss: closeCtxMenu,
    onJumpToUnread: () => {
      try {
        const feed = feedRef.current;
        if (feed) feed.scrollTop = feed.scrollHeight;
      } catch {}
      const lastId = Number(chatMeta?.last_message_id || 0);
      if (lastId > 0) postReadUpTo(lastId);
    },
  });

  useEffect(() => {
    // On desktop we render ChatInputBar directly inside the desktop view.
    // The BottomNavigation slot is MOBILE-only.
    if (isDesktop) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      slotKeyRef.current = null;
      return;
    }

    if (imageViewer?.src || ctxMenu?.item) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      return;
    }

    if (deferInputUntilReady) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      return;
    }

    const slotKeySource =
      resolvedDirectChatId ||
      (directCode ? `code:${directCode}` : "") ||
      directChatIdFromChat ||
      rawParamId ||
      "";

    const slotKey = `direct:${String(slotKeySource || "")}`;

    if (slotKeyRef.current !== slotKey) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      slotKeyRef.current = slotKey;
    }

    if (!chatInputSlotOpenedRef.current) {
      openChatInputSlotOnce();
      chatInputSlotOpenedRef.current = true;
    }

    return () => {
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
      slotKeyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    resolvedDirectChatId,
    directCode,
    directChatIdFromChat,
    rawParamId,
    imageViewer?.src,
    ctxMenu?.item,
    deferInputUntilReady,
  ]);

  useEffect(() => {
    const active = !isDesktop && Boolean(imageViewer?.src || ctxMenu?.item);
    setBottomNavSuppressed(active);

    return () => {
      if (active) setBottomNavSuppressed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, imageViewer?.src, ctxMenu?.item]);

  useEffect(() => {
    if (isDesktop) return;
    if (imageViewer?.src) return;
    if (ctxMenu?.item) return;
    if (!chatInputSlotOpenedRef.current) return;

    let raf = null;

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        updateChatInputSlotProps();
      });
    };

    schedule();

    return () => {
      try {
        if (raf) window.cancelAnimationFrame(raf);
      } catch {
        // ignore
      }
      raf = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    imageViewer?.src,
    ctxMenu?.item,
    inputValue,
    sending,
    replyTo?.id,
    editingId,
    pendingImages?.length,
    photoSharingState?.allowed,
    chatMeta?.unread_count,
    typingUsers,
    messagesBaseUrl,
    ctxMenu?.item?.id,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (resolvedDirectChatId) {
      setPreStartQuote(null);
      return;
    }
    if (eventContextId > 0) {
      setPreStartQuote(null);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/miniapp/action-pricing/", {
          params: { action: "direct_chat_start" },
        });
        if (!cancelled) setPreStartQuote(res?.data || null);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventContextId, resolvedDirectChatId]);

  const { openProfileCard } = useProfileCardModal();
  const openPeerVault = useCallback(() => {
    if (!isDeletedPeer && peer?.code) openProfileCard(peer);
  }, [isDeletedPeer, peer, openProfileCard]);
  const openProfileByCode = openProfileCard;

  // ===== Report/block handler (DM) =====
  const closeBlockModal = () => {
    setBlockModalOpen(false);
    setReportModalMode("user");
    setReportMessageTargetId(null);
    setReportCategory("");
    setReportComment("");
    setBlockSubmitting(false);
  };

  const closeManageModal = () => {
    setManageModalOpen(false);
    setManageSubmitting(false);
  };

  const handleReportClick = () => {
    setReportModalMode("user");
    setReportMessageTargetId(null);
    setReportCategory("");
    setReportComment("");
    setBlockSubmitting(false);
    setBlockModalOpen(true);
  };

  const handleManageClick = () => {
    if (!resolvedDirectChatId || manageSubmitting) return;
    setManageModalOpen(true);
  };

  const handleInlineMessageReportClick = useCallback((itemOrId) => {
    const messageId = Number(
      typeof itemOrId === "object" && itemOrId !== null
        ? itemOrId?.id
        : itemOrId
    );
    if (!messageId) return;
    setReportModalMode("message");
    setReportMessageTargetId(messageId);
    setReportCategory("");
    setReportComment("");
    setBlockSubmitting(false);
    setBlockModalOpen(true);
  }, []);

  const submitBlock = async () => {
    const category = String(reportCategory || "").trim();
    const comment = String(reportComment || "").trim();

    if (reportModalMode === "message") {
      const messageId = Number(reportMessageTargetId || 0);
      if (!messageId) {
        window.alert(t("chats.direct_chat.message_report_failed"));
        return;
      }

      setBlockSubmitting(true);
      try {
        await api.post(
          `/chats/internal-chat/messages/${encodeURIComponent(String(messageId))}/report/`,
          { description: "Anonymous direct message report from chat UI." }
        );
        setReportedInlineMessageIds((prev) => {
          const next = new Set(prev || []);
          next.add(String(messageId));
          return next;
        });
        setBlockSubmitting(false);
        setBlockModalOpen(false);
        setReportModalMode("user");
        setReportMessageTargetId(null);
        setReportCategory("");
        setReportComment("");
        try {
          window.dispatchEvent(
            new CustomEvent("global-info", {
              detail: {
                message: t("chats.direct_chat.message_report_success"),
              },
            })
          );
        } catch {}
      } catch (err) {
        setBlockSubmitting(false);
        // eslint-disable-next-line no-console
        console.error("Failed to report direct chat message", err);
        window.alert(
          err?.response?.data?.error ||
            t("chats.direct_chat.message_report_failed")
        );
      }
      return;
    }

    if (!category) {
      window.alert(t("chats.direct_chat.report_category_required"));
      return;
    }

    if (!resolvedDirectChatId) {
      // Без реального DM-чата заблокировать не можем
      window.alert(t("chats.direct_chat.block_unavailable_no_chat"));
      return;
    }

    setBlockSubmitting(true);
    try {
      const form = new FormData();
      form.append("report_category", category);
      if (comment) {
        form.append("report_comment", comment);
      }

      await api.post(
        `/chats/direct/${encodeURIComponent(String(resolvedDirectChatId))}/block/`,
        form
      );
      setIsBlocked(true);
      setBlockSubmitting(false);
      setBlockModalOpen(false);
      setReportCategory("");
      setReportComment("");
      try {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("chats.direct_chat.report_success"),
            },
          })
        );
      } catch {}
    } catch (err) {
      setBlockSubmitting(false);
      // eslint-disable-next-line no-console
      console.error("Failed to block direct chat peer", err);
      window.alert(
        err?.response?.data?.error ||
          t("chats.direct_chat.block_failed")
      );
    }
  };

  const navigateBackToChatsList = useCallback(() => {
    try {
      queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
    } catch {
      // ignore
    }

    try {
      sessionStorage.setItem("vg_open_chat_modal_after_back", "1");
      sessionStorage.setItem("vg_open_chat_modal_tab", "direct");
    } catch {
      // ignore
    }

    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  }, [navigate, onBack, queryClient]);

  const submitManageAction = useCallback(async (action) => {
    if (!resolvedDirectChatId || manageSubmitting) return;

    const normalizedAction = String(action || "").trim().toLowerCase();
    if (!["delete", "delete_and_block"].includes(normalizedAction)) return;

    setManageSubmitting(true);
    try {
      const form = new FormData();
      form.append("action", normalizedAction);

      const resp = await api.post(
        `/chats/direct/${encodeURIComponent(String(resolvedDirectChatId))}/manage/`,
        form
      );

      if (normalizedAction === "delete_and_block" || resp?.data?.is_blocked) {
        setIsBlocked(true);
      }

      setManageModalOpen(false);
      setManageSubmitting(false);

      try {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                normalizedAction === "delete_and_block"
                  ? t("chats.direct_chat.manage_delete_and_block_success")
                  : t("chats.direct_chat.manage_delete_success"),
            },
          })
        );
      } catch {
        // ignore
      }

      navigateBackToChatsList();
    } catch (err) {
      setManageSubmitting(false);
      console.error("Failed to manage direct chat", err);
      window.alert(
        err?.response?.data?.error ||
          t("chats.direct_chat.manage_failed")
      );
    }
  }, [manageSubmitting, navigateBackToChatsList, resolvedDirectChatId, t]);

  const directMessageItems = items.filter((item) => item?.type === ITEM_TYPES.MESSAGE);
  const directLastReportableMessage = [...directMessageItems]
    .reverse()
    .find((item) => {
      const messageId = Number(item?.id || 0);
      const messageType = String(item?.message_type || "").trim().toLowerCase();
      return (
        messageId > 0 &&
        !item?.is_deleted &&
        (messageType === "text" || messageType === "system_as_user")
      );
    });
  const directInlineReportMessageId =
    !isBlocked &&
    directLastReportableMessage?.id &&
    !directLastReportableMessage?.is_self &&
    !reportedInlineMessageIds.has(String(directLastReportableMessage.id))
      ? Number(directLastReportableMessage.id)
      : null;
  const firstDirectMessage = directMessageItems[0] || null;
  const hasSelfReplyInDirect = directMessageItems.some((item) => !!item?.is_self);
  const showSafetyNotice =
    !isBlocked &&
    !!firstDirectMessage &&
    !firstDirectMessage?.is_self &&
    !hasSelfReplyInDirect;
  const hasPersistedEventContextMessage = React.useMemo(() => {
    if (eventContextId <= 0) return false;
    return (items || []).some((item) => {
      if (item?.type !== ITEM_TYPES.MESSAGE) return false;
      if (String(item?.message_type || "").trim().toLowerCase() !== "system_as_user") {
        return false;
      }
      const payload = item?.payload;
      if (!payload || typeof payload !== "object") return false;
      if (String(payload?.kind || "").trim().toLowerCase() !== "event_host_contact_context") {
        return false;
      }
      const payloadEventId = Number(payload?.event_id || payload?.event?.id || 0);
      return payloadEventId === eventContextId;
    });
  }, [eventContextId, items]);
  const previewEventContextItem = React.useMemo(() => {
    if (eventContextId <= 0 || hasPersistedEventContextMessage || !eventContext) {
      return null;
    }

    return {
      type: ITEM_TYPES.MESSAGE,
      id: -eventContextId,
      created_at: null,
      author_id: currentUserId ?? null,
      message_type: "system_as_user",
      text: localEventContextActivated
        ? t("chats.direct_chat.event_context_message")
        : t("chats.direct_chat.event_context_notice"),
      time: "",
      role: null,
      is_self: true,
      sender_username: "",
      sender_code: null,
      edited_at: null,
      reply_to_id: null,
      reply_to: null,
      attachments: [],
      reactions: [],
      payload: {
        kind: "event_host_contact_context",
        event_id: eventContextId,
        event: eventContext,
      },
      read_by_peer: false,
    };
  }, [
    currentUserId,
    eventContext,
    eventContextId,
    hasPersistedEventContextMessage,
    localEventContextActivated,
    t,
  ]);
  const renderedItems = React.useMemo(
    () => (previewEventContextItem ? [...items, previewEventContextItem] : items),
    [items, previewEventContextItem]
  );

  React.useLayoutEffect(() => {
    if (isDesktop) {
      setPinSparseMobileFeed(false);
      return undefined;
    }

    let frameId = null;
    let contentObserver = null;
    let feedObserver = null;

    const measure = () => {
      frameId = null;
      const feed = feedRef.current;
      const content = feedContentRef.current;
      if (!feed || !content) return;

      const feedHeight = Math.max(0, Number(feed.clientHeight || 0));
      const contentHeight = Math.max(
        0,
        Number(content.getBoundingClientRect?.().height || content.scrollHeight || 0)
      );
      const keyboardInset = Math.max(
        0,
        Number.parseFloat(
          directChatViewportStyle?.["--vg-chat-keyboard-inset"] || "0"
        ) || 0
      );
      const inputHeight = Math.max(0, Number(getInputBarHeightPx() || 0));
      const sparseTopPadding = 32;
      const contentGap = 12;
      const bottomReserve = keyboardInset > 0
        ? keyboardInset + Math.max(inputHeight, 64) + 10
        : Math.max(0, Number(feedBottomPadPx || 0));
      const availableContentHeight = Math.max(
        0,
        feedHeight - sparseTopPadding - bottomReserve - contentGap
      );
      const hasRenderedContent = renderedItems.length > 0 || showSafetyNotice;
      const shouldPin =
        feedHeight > 0 &&
        (!hasRenderedContent || contentHeight <= availableContentHeight);

      setPinSparseMobileFeed((current) =>
        current === shouldPin ? current : shouldPin
      );
    };

    const scheduleMeasure = () => {
      if (frameId != null) return;
      frameId = window.requestAnimationFrame(measure);
    };

    measure();
    try {
      if (typeof ResizeObserver !== "undefined") {
        contentObserver = new ResizeObserver(scheduleMeasure);
        contentObserver.observe(feedContentRef.current);
        feedObserver = new ResizeObserver(scheduleMeasure);
        feedObserver.observe(feedRef.current);
      }
    } catch {
      // A dependency-driven measurement below still keeps the layout correct.
    }

    window.addEventListener("resize", scheduleMeasure, { passive: true });
    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      contentObserver?.disconnect?.();
      feedObserver?.disconnect?.();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [
    directChatViewportStyle,
    feedBottomPadPx,
    isDesktop,
    renderedItems,
    showSafetyNotice,
  ]);

  // ===== Seen-by panel (kept for parity; backend may return 1 viewer in direct chats) =====
  const [seenPanel, setSeenPanel] = useState({
    open: false,
    messageId: null,
    loading: false,
    users: [],
    count: 0,
    error: null,
  });
  const seenCacheRef = useRef({});

  const closeSeenPanel = () => {
    setSeenPanel({
      open: false,
      messageId: null,
      loading: false,
      users: [],
      count: 0,
      error: null,
    });
  };

  const applySeenSnapshot = useCallback((messageId, users, count) => {
    const normalizedId = Number(messageId || 0);
    if (!normalizedId) return;

    const safeUsers = Array.isArray(users) ? users : [];
    const safeCount = Number(count || 0) || 0;

    seenCacheRef.current[String(normalizedId)] = {
      users: safeUsers,
      count: safeCount,
    };

    setItems((prev) =>
      prev.map((item) =>
        Number(item?.id || 0) === normalizedId
          ? { ...item, seen_users: safeUsers, seen_count: safeCount }
          : item
      )
    );

    setCtxMenu((prev) =>
      prev && Number(prev?.item?.id || 0) === normalizedId
        ? {
            ...prev,
            item: {
              ...prev.item,
              seen_users: safeUsers,
              seen_count: safeCount,
            },
          }
        : prev
    );
  }, []);

  const fetchSeenByMessage = async (messageId) => {
    if (!messageId) return { users: [], count: 0 };

    const resp = await api.get(`/chats/internal/messages/${messageId}/seen/`);
    const data = resp?.data;

    const list =
      (Array.isArray(data?.seen) && data.seen) ||
      (Array.isArray(data?.viewers) && data.viewers) ||
      (Array.isArray(data?.users) && data.users) ||
      (Array.isArray(data?.seen_by) && data.seen_by) ||
      (Array.isArray(data) && data) ||
      [];

    const users = list
      .map((u) => {
        const code =
          (u?.code || u?.user_code || u?.author_code || u?.profile_code || "").toString();
        const usernameRaw =
          u?.username ||
          u?.user_username ||
          u?.author_username ||
          u?.handle ||
          u?.user?.username ||
          u?.user?.handle ||
          u?.profile?.username ||
          "";

        const username = usernameRaw ? String(usernameRaw) : "";
        const isDeletedUser = isDeletedUsername(username) || isDeletedAccountUser(u);

        const avatarSrc = isDeletedUser ? null : resolveUserAvatarSrc(u);

        return {
          id: u?.id ?? u?.user_id ?? null,
          user_id: u?.user_id ?? u?.id ?? null,
          code: isDeletedUser ? null : code || null,
          username: isDeletedUser ? DELETED_USER_LABEL : username || null,
          is_self: !!(u?.is_self || u?.is_me || u?.me),
          avatarSrc,
          has_premium_subscription: !isDeletedUser && Boolean(u?.has_premium_subscription),
        };
      })
      .filter(Boolean);

    const count =
      Number(data?.count || data?.seen_count || data?.viewers_count || users.length || 0) || 0;

    applySeenSnapshot(messageId, users, count);
    return { users, count };
  };

  const toggleSeenPanel = async (messageId, options = {}) => {
    if (!messageId) return;

    const forceOpen = !!options?.force;
    const cached = seenCacheRef.current[String(messageId)];

    setSeenPanel((prev) => {
      if (!forceOpen && prev?.open && prev?.messageId === messageId) {
        return {
          open: false,
          messageId: null,
          loading: false,
          users: [],
          count: 0,
          error: null,
        };
      }

      return {
        open: true,
        messageId,
        loading: !cached,
        users: cached?.users || [],
        count: cached?.count || 0,
        error: null,
      };
    });

    try {
      const { users, count } = await fetchSeenByMessage(messageId);
      setSeenPanel((prev) => {
        if (!prev?.open || prev?.messageId !== messageId) return prev;
        return {
          ...prev,
          loading: false,
          users,
          count,
          error: null,
        };
      });
    } catch {
      setSeenPanel((prev) => {
        if (!prev?.open || prev?.messageId !== messageId) return prev;
        return {
          ...prev,
          loading: false,
          users: [],
          error: "failed",
        };
      });
    }
  };

  const closeCtxMenu = () => {
    setCtxMenu(null);
    closeSeenPanel();
  };

  const openImageViewer = (src, item = null) => {
    if (!src) return;
    closeCtxMenu();
    if (!isDesktop && chatInputSlotOpenedRef.current) {
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
    }
    if (!isDesktop) setBottomNavSuppressed(true);

    try {
      inputRef.current?.blur?.();
    } catch {
      // ignore
    }

    setImageViewer({ src: String(src), item });
  };

  const closeImageViewer = () => {
    setImageViewer(null);
  };

  const jumpToMessage = (id) => {
    const targetId = Number(id || 0);
    if (!targetId) return;
    const el = feedRef.current?.querySelector?.(`[data-msg-id="${targetId}"]`);
    if (!el) {
      pendingJumpTargetRef.current = targetId;
      pendingJumpAttemptsRef.current = 0;
      if (hasOlder && !loadingOlder) {
        pendingJumpAttemptsRef.current = 1;
        void loadOlderMessagesRef.current?.();
      }
      return;
    }
    pendingJumpTargetRef.current = null;
    pendingJumpAttemptsRef.current = 0;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      // ignore
    }
    setFlashId(targetId);
    window.setTimeout(() => setFlashId(null), 1200);
  };

  // ===== Read pointer =====
  const postReadUpTo = useCallback(async (upToId) => {
    const baseUrl = messagesBaseUrlRef.current || messagesBaseUrl;
    if (!baseUrl) return;
    if (!upToId || upToId <= 0) return;
    if (upToId <= lastSentReadUpToRef.current) return;

    // Preserve the read point across a close/reopen while the API request is
    // still in flight, which is common on mobile navigation.
    rememberChatRead(baseUrl, upToId);

    const form = new FormData();
    form.append("message_id", String(upToId));

    try {
      const resp = await api.post(`${baseUrl}/read-up-to/`, form, {
        meta: { suppressGlobalError: true },
      });
      const payload = resp?.data || {};

      // Ignore late UI state updates from a conversation that was already left.
      if (messagesBaseUrlRef.current && messagesBaseUrlRef.current !== baseUrl) return;

      lastSentReadUpToRef.current = Math.max(lastSentReadUpToRef.current, upToId);

      setChatMeta((prev) => ({
        ...prev,
        last_read_message_id: Number(
          payload?.last_read_message_id || prev.last_read_message_id || 0
        ),
        peer_last_read_message_id: Number(
          payload?.peer_last_read_message_id || prev.peer_last_read_message_id || 0
        ),
        unread_count: Number(payload?.unread_count || 0),
      }));

      try {
        queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
      } catch {
        // ignore
      }
    } catch {
      // best-effort
    }
  }, [messagesBaseUrl, queryClient]);

  const scheduleReadUpTo = (upToId) => {
    if (!upToId || upToId <= 0) return;
    if ((chatMeta?.unread_count || 0) <= 0) return;

    // Keep the position immediately, rather than waiting for the debounced
    // API write, so a quick close/reopen cannot restore an old unread anchor.
    rememberChatRead(messagesBaseUrlRef.current || messagesBaseUrl, upToId);

    if (readUpToTimerRef.current) return;

    readUpToTimerRef.current = window.setTimeout(() => {
      readUpToTimerRef.current = null;
      postReadUpTo(upToId);
    }, 700);
  };

  const computeMaxVisibleMessageId = () => {
    const feed = feedRef.current;
    if (!feed) return 0;

    const cRect = feed.getBoundingClientRect();
    const nodes = feed.querySelectorAll("[data-msg-id]");

    let maxId = 0;
    nodes.forEach((el) => {
      const r = el.getBoundingClientRect();
      const visible = r.bottom > cRect.top && r.top < cRect.bottom;
      if (!visible) return;

      const id = Number(el.getAttribute("data-msg-id") || 0);
      if (id > maxId) maxId = id;
    });

    return maxId;
  };

  const loadOlderMessages = useCallback(async () => {
    const baseUrl = messagesBaseUrlRef.current || messagesBaseUrl;
    if (!baseUrl) return;
    if (loadingOlder) return;
    if (isPrependingRef.current) return;
    if (!hasOlder) return;
    if (!items.length) return;

    const oldestId = findOldestChatItemId(items);
    if (!oldestId || oldestId <= 0) return;

    setLoadingOlder(true);
    isPrependingRef.current = true;
    let prependPosition = null;

    try {
      const eventContextMessageText = t("chats.direct_chat.event_context_message");
      const resp = await api.get(`${baseUrl}/messages/`, {
        params: { before_id: oldestId, limit: pendingJumpTargetRef.current ? 200 : PAGE_LIMIT },
      });

      const serverMessages = resp?.data?.messages || [];
      const meta = resp?.data?.meta || {};
      setHasOlder(Boolean(meta?.has_older));

      if (!serverMessages.length) {
        setLoadingOlder(false);
        return;
      }

      const mappedOlder = serverMessages.map((m) => {
        if (m.message_type === "system") {
          return {
            type: ITEM_TYPES.SYSTEM,
            id: m.id,
            text: m.text || "",
            payload: m.payload || null,
            lines: String(m.text || "").split("\n"),
          };
        }

        const rawAtt = Array.isArray(m.attachments) ? m.attachments : [];
        const attachments = rawAtt
          .map((a) => {
            const t = a?.type || a?.attachment_type || null;
            const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
            if (!t || !url) return null;
            return {
              type: t,
              url,
              width: a?.width ?? null,
              height: a?.height ?? null,
              original_name: a?.original_name ?? null,
              size_bytes: a?.size_bytes ?? null,
            };
          })
          .filter(Boolean);

        return {
          type: ITEM_TYPES.MESSAGE,
          created_at: m.created_at || null,
          id: m.id,
          author_id: m.author_id ?? m.author?.id ?? null,
          message_type: m.message_type || "text",
          payload: hydrateDirectEventContextPayload(
            m,
            eventContext,
            eventContextId,
            eventContextMessageText
          ),
          text: m.text,
          time: formatShortTime(m.created_at),
          role: m.author_role || null,
          author_is_deleted_account: !!m.author_is_deleted_account,
          is_self: !!m.is_self,
          sender_username: m.author_username || "@user",
          sender_code: m.author_code || null,
          edited_at: m.edited_at || null,
          reply_to_id: m.reply_to_id || null,
          reply_to: m.reply_to || null,
          attachments,
          reactions: normalizeMessageReactions(m.reactions, currentUserId),
          read_by_peer:
            !!m.is_self &&
            Number(m.id || 0) > 0 &&
            Number(m.id || 0) <= Number(peerLastReadRef.current || 0),
        };
      });

      prependPosition = captureChatPrependPosition(feedRef.current);
      // Commit the new rows and restore the viewport anchor in the same frame.
      // Waiting for requestAnimationFrame here exposes one broken intermediate
      // frame and is the visible flash users notice while loading history.
      flushSync(() => {
        setItems((prev) => prependOlderChatPage(prev, mappedOlder));
      });
      restoreChatPrependPosition(feedRef.current, prependPosition);
      isPrependingRef.current = false;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load older direct chat messages", err);
    } finally {
      setLoadingOlder(false);
      isPrependingRef.current = false;
    }
  }, [currentUserId, eventContext, eventContextId, hasOlder, items, loadingOlder, messagesBaseUrl, t]);
  loadOlderMessagesRef.current = loadOlderMessages;

  useEffect(() => {
    const targetId = Number(pendingJumpTargetRef.current || 0);
    if (!targetId) return;

    const el = feedRef.current?.querySelector?.(`[data-msg-id="${targetId}"]`);
    if (el) {
      pendingJumpTargetRef.current = null;
      pendingJumpAttemptsRef.current = 0;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        // ignore
      }
      setFlashId(targetId);
      window.setTimeout(() => setFlashId(null), 1200);
      return;
    }

    if (loadingOlder) return;

    if (!hasOlder || pendingJumpAttemptsRef.current >= 100) {
      pendingJumpTargetRef.current = null;
      pendingJumpAttemptsRef.current = 0;
      return;
    }

    pendingJumpAttemptsRef.current += 1;
    void loadOlderMessagesRef.current?.();
  }, [items, hasOlder, loadingOlder]);

  const onFeedScroll = () => {
    const feed = feedRef.current;
    if (feed && !loadingOlder && !isPrependingRef.current && hasOlder) {
      try {
        if (feed.scrollTop <= 72) {
          loadOlderMessages();
        }
      } catch {
        // ignore
      }
    }

    if (rafScrollRef.current) return;
    rafScrollRef.current = window.requestAnimationFrame(() => {
      rafScrollRef.current = null;
      const maxVisibleId = computeMaxVisibleMessageId();
      if (maxVisibleId > 0) scheduleReadUpTo(maxVisibleId);
    });
  };

  useEffect(() => {
    return () => {
      try {
        if (rafScrollRef.current) window.cancelAnimationFrame(rafScrollRef.current);
      } catch {
        // ignore
      }
      rafScrollRef.current = null;

      try {
        if (readUpToTimerRef.current) window.clearTimeout(readUpToTimerRef.current);
      } catch {
        // ignore
      }
      readUpToTimerRef.current = null;
    };
  }, []);

  useEffect(() => {
    messagesBaseUrlRef.current = messagesBaseUrl;
  }, [messagesBaseUrl]);

  useEffect(() => {
    pendingJumpTargetRef.current = null;
    pendingJumpAttemptsRef.current = 0;
  }, [messagesBaseUrl, pushUnreadFocusKey]);

  useEffect(() => {
    lastMessageIdRef.current = Number(chatMeta?.last_message_id || 0);
  }, [chatMeta?.last_message_id]);

  useEffect(() => {
    setLocalEventContextActivated(false);
  }, [eventContextId, messagesBaseUrl]);

  useEffect(() => {
    return () => {
      const baseUrl = messagesBaseUrlRef.current;
      const lastId = Number(lastMessageIdRef.current || 0);

      if (baseUrl && lastId > 0 && lastId > lastSentReadUpToRef.current) {
        try {
          const form = new FormData();
          form.append("message_id", String(lastId));

          api
            .post(`${baseUrl}/read-up-to/`, form, {
              meta: { suppressGlobalError: true },
            })
            .then((resp) => {
              const payload = resp?.data || {};
              lastSentReadUpToRef.current = Math.max(lastSentReadUpToRef.current, lastId);
              setChatMeta((prev) => ({
                ...prev,
                last_read_message_id: Number(
                  payload?.last_read_message_id || prev?.last_read_message_id || 0
                ),
                unread_count: Number(payload?.unread_count || 0),
              }));
              try {
                queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
              } catch {
                // ignore
              }
            })
            .catch(() => {});
        } catch {
          // ignore
        }
      } else {
        try {
          queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
        } catch {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Load messages =====
  useEffect(() => {
    if (!messagesBaseUrl) return;

    let cancelled = false;
    const controller = new AbortController();

    const settleInitialPosition = (nextMeta, { empty = false } = {}) => {
      if (cancelled || didInitialScrollRef.current) return;
      initialPositionCleanupRef.current?.();
      initialPositionCleanupRef.current = stabilizeChatInitialPosition({
        feedOrRef: feedRef,
        meta: nextMeta,
        focusFirstUnread: focusFirstUnreadFromPush,
        empty,
        onPendingMessage: (messageId) => {
          pendingJumpTargetRef.current = messageId;
        },
        onSettled: () => {
          if (cancelled) return;
          didInitialScrollRef.current = true;
          setInitialPositionSettled(true);
        },
      });
    };

    async function loadMessages() {
      try {
        const eventContextMessageText = t("chats.direct_chat.event_context_message");
        const resp = await loadChatMessagePage(messagesBaseUrl, PAGE_LIMIT, {
          signal: controller.signal,
        });
        if (cancelled) return;

        const serverMessages = resp?.data?.messages || [];
        const isRecoverySync = latestLoadedBaseUrlRef.current === messagesBaseUrl;
        latestLoadedBaseUrlRef.current = messagesBaseUrl;

        const meta = resp?.data?.meta || {};
        setPhotoSharingState(normalizePhotoSharingState(meta?.photo_sharing));
        if (typeof meta.is_blocked === "boolean") {
          setIsBlocked(meta.is_blocked);
        }
        const serverMeta = {
          ...applyChatPinnedMessages({}, meta),
          last_read_message_id: Number(meta?.last_read_message_id || 0),
          peer_last_read_message_id: Number(meta?.peer_last_read_message_id || 0),
          unread_count: Number(meta?.unread_count || 0),
          first_unread_message_id: Number(meta?.first_unread_message_id || 0),
          last_message_id: Number(meta?.last_message_id || 0),
          notifications_muted: Boolean(meta?.notifications_muted),
        };
        const nextMeta = reconcileChatReadPosition(messagesBaseUrl, serverMeta);
        const firstUnreadTarget = resolveFirstUnreadMessageId(
          nextMeta,
          focusFirstUnreadFromPush || Number(nextMeta?.unread_count || 0) > 0
        );
        if (firstUnreadTarget) {
          pendingJumpTargetRef.current = firstUnreadTarget;
          pendingJumpAttemptsRef.current = 0;
        }
        setChatMeta(nextMeta);
        setHasOlder(Boolean(meta?.has_older));

        // Reading and visual positioning are separate: the snapshot is
        // acknowledged here, while the entry stabilizer keeps caught-up chats
        // pinned to the actual bottom as their content finishes rendering.
        const openReadTarget = resolveChatOpenReadTarget(nextMeta);
        if (openReadTarget > 0) void postReadUpTo(openReadTarget);

        if (!serverMessages.length) {
          const shouldPreserveOptimistic =
            preserveOptimisticOnEmptyLoadRef.current === messagesBaseUrl;
          setItems((prev) => {
            if (isRecoverySync) return prev;
            if (shouldPreserveOptimistic && Array.isArray(prev) && prev.length > 0) {
              return prev;
            }
            return [];
          });
          setInitialMessagesSettled(true);
          settleInitialPosition(nextMeta, { empty: true });
          return;
        }
        if (preserveOptimisticOnEmptyLoadRef.current === messagesBaseUrl) {
          preserveOptimisticOnEmptyLoadRef.current = null;
        }

        const mapped = serverMessages.map((m) => {
          if (m.message_type === "system") {
            return {
              type: ITEM_TYPES.SYSTEM,
              id: m.id,
              text: m.text || "",
              payload: m.payload || null,
              lines: String(m.text || "").split("\n"),
            };
          }

          const rawAtt = Array.isArray(m.attachments) ? m.attachments : [];
          const attachments = rawAtt
            .map((a) => {
              const t = a?.type || a?.attachment_type || null;
              const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
              if (!t || !url) return null;
              return {
                type: t,
                url,
                width: a?.width ?? null,
                height: a?.height ?? null,
                original_name: a?.original_name ?? null,
                size_bytes: a?.size_bytes ?? null,
              };
            })
            .filter(Boolean);

          return {
            type: ITEM_TYPES.MESSAGE,
            created_at: m.created_at || null,
            id: m.id,
            author_id: m.author_id ?? m.author?.id ?? null,
            message_type: m.message_type || "text",
            payload: hydrateDirectEventContextPayload(
              m,
              eventContext,
              eventContextId,
              eventContextMessageText
            ),
            text: m.text,
            time: formatShortTime(m.created_at),
            role: m.author_role || null,
            author_is_deleted_account: !!m.author_is_deleted_account,
            is_self: !!m.is_self,
            sender_username: m.author_username || "@user",
            sender_code: m.author_code || null,
            edited_at: m.edited_at || null,
            reply_to_id: m.reply_to_id || null,
            reply_to: m.reply_to || null,
            attachments,
            reactions: normalizeMessageReactions(m.reactions, currentUserIdRef.current),
            read_by_peer:
              !!m.is_self &&
              Number(m.id || 0) > 0 &&
              Number(m.id || 0) <= Number(nextMeta.peer_last_read_message_id || 0),
          };
        });

        setItems((prev) =>
          isRecoverySync ? mergeLatestChatPage(prev, mapped) : mapped
        );
        setInitialMessagesSettled(true);
        settleInitialPosition(nextMeta);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("Failed to load direct chat messages", err);
        if (!cancelled) {
          if (onInitialError && !initialReadyNotifiedRef.current) {
            onInitialError();
          } else if (!onInitialError) {
            setInitialMessagesSettled(true);
            setInitialPositionSettled(true);
          }
        }
      }
    }

    loadMessages();

    return () => {
      cancelled = true;
      controller.abort();
      // WebSocket open/focus recovery refreshes this effect. Do not detach the
      // entry-position observer here: it must survive until the chat identity
      // changes (or the stabilizer itself reaches its timeout/user gesture).
    };
  }, [
    eventContext,
    eventContextId,
    focusFirstUnreadFromPush,
    latestSyncVersion,
    messagesBaseUrl,
    onInitialError,
    postReadUpTo,
    pushUnreadFocusKey,
    t,
  ]);

  useEffect(() => {
    peerLastReadRef.current = Number(chatMeta?.peer_last_read_message_id || 0);
  }, [chatMeta?.peer_last_read_message_id]);

  useEffect(() => {
    const peerLastRead = Number(chatMeta?.peer_last_read_message_id || 0);
    if (peerLastRead <= 0) return;

    setItems((prev) => {
      let changed = false;
      const next = (prev || []).map((it) => {
        if (!it || it.type !== ITEM_TYPES.MESSAGE || !it.is_self) return it;

        const mid = Number(it.id || 0);
        if (mid <= 0) return it;

        const shouldBeRead = mid <= peerLastRead;
        if (Boolean(it.read_by_peer) === shouldBeRead) return it;

        changed = true;
        return { ...it, read_by_peer: shouldBeRead };
      });

      return changed ? next : prev;
    });
  }, [chatMeta?.peer_last_read_message_id]);

  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    const isNearBottom = () => {
      try {
        return feed.scrollHeight - (feed.scrollTop + feed.clientHeight) < 120;
      } catch {
        return false;
      }
    };

    const shouldAutoScroll = justSentRef.current || isNearBottom();

    if (shouldAutoScroll) {
      scheduleChatFeedScrollToBottom(feedRef);
    }

    justSentRef.current = false;
  }, [items.length]);

  // ===== Layout observers =====
  useEffect(() => {
    let raf = null;

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        recomputeFeedBottomPadding();
      });
    };

    schedule();
    const t1 = window.setTimeout(schedule, 80);
    const t2 = window.setTimeout(schedule, 220);

    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("orientationchange", schedule, { passive: true });

    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", schedule);
    vv?.addEventListener?.("scroll", schedule);

    let roNav = null;
    let roFeed = null;

    try {
      if (typeof ResizeObserver !== "undefined") {
        const nav = document.getElementById("bottom-nav");
        if (nav) {
          roNav = new ResizeObserver(schedule);
          roNav.observe(nav);
        }

        if (feedRef.current) {
          roFeed = new ResizeObserver(schedule);
          roFeed.observe(feedRef.current);
        }
      }
    } catch {
      // ignore
    }

    return () => {
      try {
        if (raf) window.cancelAnimationFrame(raf);
      } catch {
        // ignore
      }
      raf = null;

      try {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      } catch {
        // ignore
      }

      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      vv?.removeEventListener?.("resize", schedule);
      vv?.removeEventListener?.("scroll", schedule);

      try {
        roNav?.disconnect?.();
      } catch {
        // ignore
      }
      try {
        roFeed?.disconnect?.();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => recomputeFeedBottomPadding());
    return () => {
      try {
        window.cancelAnimationFrame(id);
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // ===== Context menu positioning refresh =====
  useEffect(() => {
    const id = ctxMenu?.item?.id;
    if (!id) return;

    let raf = null;

    const updateRect = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;

        const rowEl = feedRef.current?.querySelector?.(`[data-msg-id="${id}"]`);
        const bubbleEl =
          feedRef.current?.querySelector?.(`[data-bubble-id="${id}"]`) || rowEl;

        if (!rowEl || !bubbleEl) return;

        const rowRect = rowEl.getBoundingClientRect();
        const bubbleRect = bubbleEl.getBoundingClientRect();
        const overlayScopeEl = bubbleEl.closest?.("[data-chat-overlay-scope]");
        const overlayScopeRect = overlayScopeEl?.getBoundingClientRect?.();
        const overlayFeedRect = feedRef.current?.getBoundingClientRect?.();
        const overlayBodyRect = overlayScopeEl
          ?.querySelector?.("[data-chat-overlay-body]")
          ?.getBoundingClientRect?.();
        const overlayHeaderEl = overlayScopeEl?.querySelector?.("[data-chat-overlay-header]");
        const overlayHeaderRect = overlayHeaderEl?.getBoundingClientRect?.();
        const overlayHeaderBorderRadius = overlayHeaderEl
          ? window.getComputedStyle(overlayHeaderEl).borderRadius
          : null;

        setCtxMenu((prev) => {
          if (!prev || prev?.item?.id !== id) return prev;
          return {
            ...prev,
            rowRect,
            bubbleRect,
            overlayScopeRect: overlayScopeRect || prev.overlayScopeRect || null,
            overlayFeedRect: overlayFeedRect || prev.overlayFeedRect || null,
            overlayBodyRect: overlayBodyRect || prev.overlayBodyRect || null,
            overlayHeaderRect: overlayHeaderRect || prev.overlayHeaderRect || null,
            overlayHeaderBorderRadius:
              overlayHeaderBorderRadius || prev.overlayHeaderBorderRadius || null,
          };
        });
      });
    };

    updateRect();

    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", updateRect);
    vv?.addEventListener?.("scroll", updateRect);
    window.addEventListener?.("resize", updateRect);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      vv?.removeEventListener?.("resize", updateRect);
      vv?.removeEventListener?.("scroll", updateRect);
      window.removeEventListener?.("resize", updateRect);
    };
  }, [ctxMenu?.item?.id]);

  // ===== Actions =====
  const copyMessage = async (text) => {
    const value = (text || "").toString();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        // ignore
      }
    }
  };

  const beginReply = (item) => {
    if (!item?.id) return;

    const rawAuthor = (item.sender_username || item.author_username || "@user").toString();
    const author =
      isDeletedUsername(rawAuthor) || isDeletedAccountUser(item)
        ? DELETED_USER_LABEL
        : formatUserHandle(rawAuthor);
    const raw = (item.text || "").toString();
    const compact = raw.replace(/\s+/g, " ").trim();
    const preview = compact.length > 70 ? `${compact.slice(0, 70)}…` : compact;

    setReplyTo({ id: item.id, preview, author });
    setEditingId(null);
    setEditingMeta(null);
    closeCtxMenu();
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const beginEdit = (item) => {
    if (!item?.id || isChatPollPayload(item?.payload)) return;

    const raw = (item.text || "").toString();
    const compact = raw.replace(/\s+/g, " ").trim();
    const preview = compact.length > 70 ? `${compact.slice(0, 70)}…` : compact;

    setEditingId(item.id);
    setEditingMeta({ id: item.id, preview });
    setReplyTo(null);
    closeCtxMenu();
    setInputValue(raw);

    setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);
  };

  const canEdit = (item) => !!item?.is_self && !isChatPollPayload(item?.payload);
  const canDelete = (item) => !!item?.is_self || currentUserIsSuperuser;

  const applyReactionSnapshot = useCallback(
    (messageId, rawReactions) => {
      const normalizedId = Number(messageId || 0);
      if (!normalizedId) return;

      const reactions = normalizeMessageReactions(rawReactions, currentUserId);
      setItems((prev) =>
        prev.map((item) =>
          Number(item?.id || 0) === normalizedId ? { ...item, reactions } : item
        )
      );
      setCtxMenu((prev) =>
        prev && Number(prev?.item?.id || 0) === normalizedId
          ? { ...prev, item: { ...prev.item, reactions } }
          : prev
      );
    },
    [currentUserId]
  );

  const togglePinnedMessage = async (item) => {
    if (!item?.id) return;
    try {
      const { data } = await api.post(`/chats/internal-chat/messages/${item.id}/pin/`);
      setChatMeta((prev) => applyChatPinnedMessages(prev, data));
    } catch (err) {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message: err?.response?.data?.code === "pin_limit_reached"
            ? t("chats.event_chat.pinned.limit")
            : err?.response?.data?.error || t("chats.event_chat.pinned.failed"),
          status: err?.response?.status,
        },
      }));
    }
  };

  const deleteMessage = async (item) => {
    if (!item?.id) return false;
    try {
      await api.post(`/chats/internal-chat/messages/${item.id}/delete/`);
      setItems((prev) => prev.filter((m) => m?.id !== item.id));
      setChatMeta((prev) => removeChatPinnedMessage(prev, item.id));
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to delete direct message", err);
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message:
              err?.response?.data?.error ||
              t("chats.event_chat.ctx.delete_failed", {
                defaultValue: "Could not delete this message.",
              }),
            status: err?.response?.status,
          },
        })
      );
      return false;
    } finally {
      closeCtxMenu();
    }
  };

  const toggleReaction = useCallback(
    async (item, emoji) => {
      const messageId = Number(item?.id || 0);
      if (!messageId || !emoji) return;

      try {
        const resp = await api.post(`/chats/internal-chat/messages/${messageId}/react/`, {
          emoji,
        });
        const payload = resp?.data?.message;
        if (payload?.id) {
          applyReactionSnapshot(payload.id, payload.reactions);
        }
      } catch (err) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.error ||
          t("chats.event_chat.reactions.update_failed");

        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message, status },
          })
        );
      }
    },
    [applyReactionSnapshot, t]
  );

  const openCtxMenu = ({
    item,
    anchorEl,
    rowEl,
    openDetailPanel = null,
    reactionEmoji = null,
  }) => {
    if (!item || !anchorEl) return;

    try {
      inputRef.current?.blur?.();
    } catch {
      // ignore
    }
    if (!isDesktop && chatInputSlotOpenedRef.current) {
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
    }
    if (!isDesktop) setBottomNavSuppressed(true);

    closeSeenPanel();

    const bubbleRect = anchorEl.getBoundingClientRect();
    const rowRect = (rowEl || anchorEl).getBoundingClientRect();
    const overlayScopeEl = anchorEl.closest?.("[data-chat-overlay-scope]");
    const overlayScopeRect = overlayScopeEl?.getBoundingClientRect?.();
    const overlayFeedRect = feedRef.current?.getBoundingClientRect?.();
    const overlayBodyRect = overlayScopeEl
      ?.querySelector?.("[data-chat-overlay-body]")
      ?.getBoundingClientRect?.();
    const overlayHeaderEl = overlayScopeEl?.querySelector?.("[data-chat-overlay-header]");
    const overlayHeaderRect = overlayHeaderEl?.getBoundingClientRect?.();
    const overlayHeaderBorderRadius = overlayHeaderEl
      ? window.getComputedStyle(overlayHeaderEl).borderRadius
      : null;

    setCtxMenu({
      item: { ...item, seen_users: item?.seen_users || [] },
      bubbleRect,
      rowRect,
      overlayScopeRect: overlayScopeRect || null,
      overlayFeedRect: overlayFeedRect || null,
      overlayBodyRect: overlayBodyRect || null,
      overlayHeaderRect: overlayHeaderRect || null,
      overlayHeaderBorderRadius,
      openDetailPanel,
      reactionEmoji,
    });

    if (item?.is_self && item?.id) {
      const cached = seenCacheRef.current[String(item.id)];
      if (cached?.count != null) {
        setCtxMenu((prev) =>
          prev
            ? {
                ...prev,
                item: {
                  ...prev.item,
                  seen_users: cached.users || [],
                  seen_count: cached.count,
                },
              }
            : prev
        );
      }

      fetchSeenByMessage(item.id).catch(() => {});
    }
  };

  const clearTypingUsers = useCallback(() => {
    const timers = typingTimersRef.current || {};
    Object.values(timers).forEach((tid) => {
      try {
        window.clearTimeout(tid);
      } catch {
        // ignore
      }
    });
    typingTimersRef.current = {};
    setTypingUsers([]);
  }, []);

  const upsertTypingUser = useCallback(
    (rawUser, isTyping) => {
      const userId = Number(rawUser?.id || 0);
      if (!userId) return;
      if (
        currentUserIdRef.current != null &&
        userId === Number(currentUserIdRef.current)
      ) return;

      const nextUser = {
        id: userId,
        username: rawUser?.username || null,
        code: rawUser?.code || null,
        avatar: rawUser?.avatar || null,
        avatar_custom_url: rawUser?.avatar_custom_url
          ? toApiAbsoluteUrl(rawUser.avatar_custom_url)
          : null,
        avatar_url: rawUser?.avatar_url ? toApiAbsoluteUrl(rawUser.avatar_url) : null,
      };

      const key = String(userId);
      const timers = typingTimersRef.current || {};
      if (timers[key]) {
        try {
          window.clearTimeout(timers[key]);
        } catch {
          // ignore
        }
        delete timers[key];
      }

      if (!isTyping) {
        setTypingUsers((prev) => prev.filter((u) => Number(u?.id || 0) !== userId));
        typingTimersRef.current = timers;
        return;
      }

      setTypingUsers((prev) => {
        const rest = prev.filter((u) => Number(u?.id || 0) !== userId);
        return [...rest, nextUser];
      });

      timers[key] = window.setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => Number(u?.id || 0) !== userId));
        delete typingTimersRef.current[key];
      }, 4500);
      typingTimersRef.current = timers;
    },
    []
  );

  const handleRealtimeEvent = useCallback(
    (payload) => {
      const event = String(payload?.event || "").toLowerCase();
      if (!event) return;

      if (event === "presence.updated") {
        const nextPresence = resolveDirectPeerPresenceUpdate(
          payload,
          peerUserIdRef.current
        );
        if (nextPresence) setPeerPresence(nextPresence);
        return;
      }

      if (event === "typing") {
        upsertTypingUser(payload?.user, !!payload?.is_typing);
        return;
      }

      if (event === "message.pinned") {
        setChatMeta((prev) => applyChatPinnedMessages(prev, payload));
        return;
      }

      if (event === "read.updated") {
        const userId = Number(payload?.user_id || 0);
        const lastRead = Number(payload?.last_read_message_id || 0);
        if (!userId || !lastRead) return;

        setChatMeta((prev) => {
          if (
            currentUserIdRef.current != null &&
            userId === Number(currentUserIdRef.current)
          ) {
            return {
              ...prev,
              last_read_message_id: Math.max(Number(prev?.last_read_message_id || 0), lastRead),
            };
          }
          return {
            ...prev,
            peer_last_read_message_id: Math.max(Number(prev?.peer_last_read_message_id || 0), lastRead),
          };
        });
        return;
      }

      if (event === "message.deleted") {
        const messageId = Number(payload?.message_id || 0);
        if (!messageId) return;
        setItems((prev) => prev.filter((m) => Number(m?.id || 0) !== messageId));
        setChatMeta((prev) => removeChatPinnedMessage(prev, messageId));
        return;
      }

      if (event !== "message.created" && event !== "message.updated") {
        return;
      }

      const m = payload?.message || {};
      const messageId = Number(m?.id || 0);
      if (!messageId) return;
      if (String(m?.payload?.kind || "").trim().toLowerCase() === "direct_photo_consent") {
        setPhotoSharingState((previous) =>
          mergePhotoSharingPayload(previous, m.payload, currentUserIdRef.current)
        );
      }
      const eventContextMessageText = t("chats.direct_chat.event_context_message");

      const authorId = Number(m?.author_id || 0);
      const viewerUserId = currentUserIdRef.current;
      const isSelf =
        typeof m?.is_self === "boolean"
          ? !!m.is_self
          : viewerUserId != null && authorId > 0 && authorId === Number(viewerUserId);

      let nextItem = null;
      if (m?.message_type === "system") {
        nextItem = {
          type: ITEM_TYPES.SYSTEM,
          id: messageId,
          text: m?.text || "",
          payload: m?.payload || null,
          lines: String(m?.text || "").split("\n"),
        };
      } else {
        nextItem = {
          type: ITEM_TYPES.MESSAGE,
          created_at: m?.created_at || null,
          id: messageId,
          client_id: m?.client_id || null,
          author_id: authorId || null,
          message_type: m?.message_type || "text",
          payload: hydrateDirectEventContextPayload(
            m,
            eventContext,
            eventContextId,
            eventContextMessageText
          ),
          text: m?.text || "",
          time: formatShortTime(m?.created_at),
          role: m?.author_role || null,
          author_is_deleted_account: !!m?.author_is_deleted_account,
          is_self: isSelf,
          sender_username: m?.author_username || "@user",
          sender_code: m?.author_code || null,
          edited_at: m?.edited_at || null,
          reply_to_id: m?.reply_to_id || null,
          reply_to: m?.reply_to || null,
          attachments: normalizeWsAttachments(m?.attachments),
          reactions: normalizeMessageReactions(m?.reactions, viewerUserId),
          read_by_peer:
            isSelf &&
            Number(messageId || 0) > 0 &&
            Number(messageId || 0) <= Number(peerLastReadRef.current || 0),
        };
      }

      setChatMeta((prev) => ({
        ...prev,
        last_message_id: Math.max(Number(prev?.last_message_id || 0), messageId),
      }));

      if (event === "message.updated") {
        setItems((prev) =>
          prev.map((item) => (Number(item?.id || 0) === messageId ? { ...item, ...nextItem } : item))
        );
      } else {
        setItems((prev) => {
          const optimisticClientId = findMatchingOptimisticClientId(prev, nextItem);
          if (optimisticClientId) {
            return reconcileOptimisticChatMessage(prev, optimisticClientId, nextItem);
          }
          return insertItemSortedById(prev, nextItem);
        });
      }
    },
    [eventContext, eventContextId, t, upsertTypingUser]
  );

  useEffect(() => {
    if (!resolvedDirectChatId) return undefined;

    const syncFromInbox = (event) => {
      const payload = event?.detail || {};
      if (
        !isInboxEventForOpenChat(payload, {
          type: "direct",
          chatId: resolvedDirectChatId,
        })
      ) {
        return;
      }

      if (payload?.message?.id) {
        handleRealtimeEvent({ event: "message.created", message: payload.message });
      }
      requestLatestSync();
    };
    const syncFromNativeState = (event) => {
      if (event?.detail?.isActive !== false) requestLatestSync();
    };
    const syncWhenVisible = () => {
      if (document.visibilityState !== "hidden") requestLatestSync();
    };

    window.addEventListener("vg:chat-inbox-updated", syncFromInbox);
    window.addEventListener("vg:native-app-state", syncFromNativeState);
    window.addEventListener("focus", requestLatestSync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener("vg:chat-inbox-updated", syncFromInbox);
      window.removeEventListener("vg:native-app-state", syncFromNativeState);
      window.removeEventListener("focus", requestLatestSync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [handleRealtimeEvent, requestLatestSync, resolvedDirectChatId]);

  const sendTypingState = useCallback((isTyping) => {
    const ws = chatWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const nextState = !!isTyping;
    const now = Date.now();
    if (nextState) {
      if (typingStateRef.current && now - typingLastSentRef.current < 2000) {
        return;
      }
      typingStateRef.current = true;
      typingLastSentRef.current = now;
    } else {
      if (!typingStateRef.current) return;
      typingStateRef.current = false;
      typingLastSentRef.current = now;
    }

    try {
      ws.send(JSON.stringify({ action: "typing", is_typing: nextState }));
    } catch {
      // ignore
    }
  }, []);

  const scheduleTypingStop = useCallback(() => {
    if (typingStopTimerRef.current) {
      try {
        window.clearTimeout(typingStopTimerRef.current);
      } catch {
        // ignore
      }
    }
    typingStopTimerRef.current = window.setTimeout(() => {
      typingStopTimerRef.current = null;
      sendTypingState(false);
    }, 2800);
  }, [sendTypingState]);

  useEffect(() => {
    if (!resolvedDirectChatId) return undefined;

    let disposed = false;

    const clearReconnect = () => {
      if (wsReconnectTimerRef.current) {
        window.clearTimeout(wsReconnectTimerRef.current);
      }
      wsReconnectTimerRef.current = null;
    };

    const closeSocket = () => {
      const ws = chatWsRef.current;
      chatWsRef.current = null;
      if (!ws) return;
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch {
        // ignore
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      if (wsReconnectTimerRef.current) return;
      const attempt = wsReconnectAttemptRef.current || 0;
      const delayMs = Math.min(20_000, 1000 * (2 ** attempt));
      wsReconnectTimerRef.current = window.setTimeout(() => {
        wsReconnectTimerRef.current = null;
        connect();
      }, delayMs);
      wsReconnectAttemptRef.current = attempt + 1;
    };

    const connect = () => {
      if (disposed) return;
      clearReconnect();
      closeSocket();

      let ws;
      try {
        ws = new WebSocket(buildWsUrl(`/ws/chats/direct/${resolvedDirectChatId}/`));
      } catch {
        scheduleReconnect();
        return;
      }

      chatWsRef.current = ws;
      ws.onopen = () => {
        wsReconnectAttemptRef.current = 0;
        requestLatestSync();
      };
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt?.data || "{}");
          handleRealtimeEvent(payload);
        } catch {
          // ignore malformed payload
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        if (disposed) return;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnect();
      sendTypingState(false);
      closeSocket();
      clearTypingUsers();
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      typingStateRef.current = false;
    };
  }, [
    resolvedDirectChatId,
    handleRealtimeEvent,
    requestLatestSync,
    sendTypingState,
    clearTypingUsers,
  ]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    persistChatDraft(value);
    if (String(value || "").trim()) {
      sendTypingState(true);
      scheduleTypingStop();
    } else {
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
    }
  };

  const handleSend = async (options = {}) => {
    const stickerId = getChatSticker(options?.stickerId)?.id || null;
    const isStickerSend = Boolean(stickerId);
    const rawDraft = String(inputRef.current?.value ?? inputValue ?? "");
    const text = isStickerSend ? "" : rawDraft.trim();
    const hasImages = !isStickerSend && pendingImages.length > 0;
    const pendingImageIdsForSend = isStickerSend
      ? []
      : pendingImages.map((image) => image?.id).filter(Boolean);

    if (!text && !hasImages && !isStickerSend) return;
    if (sending || sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    const hasPhoto = await checkProfilePhoto("send");
    if (!hasPhoto) {
      sendInFlightRef.current = false;
      return;
    }

    const hasTooLargeImage = !isStickerSend && pendingImages.some(
      (p) => Number(p?.size || 0) > CHAT_MAX_FILE_BYTES
    );
    if (hasTooLargeImage) {
      try {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: 400,
              message: t("chats.errors.file_too_large", { max_mb: CHAT_MAX_FILE_MB }),
            },
          })
        );
      } catch {
        // ignore
      }
      sendInFlightRef.current = false;
      return;
    }

    const isEditing = !!editingId;
    const draftBeforeSend = rawDraft;
    let draftClearedForSend = false;
    let sendSucceeded = false;
    let optimisticClientId = null;

    if (typingStopTimerRef.current) {
      try {
        window.clearTimeout(typingStopTimerRef.current);
      } catch {
        // ignore
      }
      typingStopTimerRef.current = null;
    }
    sendTypingState(false);
    setSending(true);

    if (!isEditing && !isStickerSend) {
      draftClearedForSend = true;
      clearChatDraft();
      if (inputRef.current) {
        inputRef.current.style.height = "";
        inputRef.current.style.overflowY = "auto";
      }
    }

    if (!isEditing) {
      const optimisticPayload = isStickerSend
        ? { kind: "sticker", sticker_id: stickerId }
        : null;
      const optimisticItem = buildOptimisticChatMessage({
        text,
        payload: optimisticPayload,
        replyTo,
        pendingImages: isStickerSend ? [] : pendingImages.slice(0, MAX_ATTACHMENTS),
        authorId: currentUserId,
        formatTime: formatShortTime,
      });
      optimisticClientId = optimisticItem.client_id;
      justSentRef.current = true;
      setItems((prev) => [...prev, optimisticItem]);
      scheduleChatFeedScrollToBottom(feedRef);
    }

    const rollbackBeforeRequest = () => {
      if (optimisticClientId) {
        setItems((prev) => removeOptimisticChatMessage(prev, optimisticClientId));
      }
      if (draftClearedForSend) {
        restoreChatDraft(draftBeforeSend);
      }
      sendInFlightRef.current = false;
      setSending(false);
    };

    let baseUrl = messagesBaseUrlRef.current || messagesBaseUrl;
    let deferredDirectChatId = null;

    // Keep a new DM virtual until its first message is accepted by the backend.
    const startingDirectChat = !baseUrl;
    if (startingDirectChat && !peer?.id && !directCode) {
      // eslint-disable-next-line no-console
      console.error("DirectChat: cannot resolve peer for direct chat", {
        rawParamId,
        directChatIdFromChat,
        directCode,
        peer,
      });
      rollbackBeforeRequest();
      return;
    }

    try {
      if (isEditing) {
        try {
          const formData = new FormData();
          formData.append("text", text);
          const resp = await api.post(`/chats/internal-chat/messages/${editingId}/edit/`, formData);
          const payload = resp?.data?.message;

          if (payload) {
            setItems((prev) =>
              prev.map((m) =>
                m?.id === editingId
                  ? {
                      ...m,
                      text: payload.text,
                      created_at: payload.created_at || m.created_at || m?.created_at || null,
                      time: formatShortTime(payload.created_at || m.created_at || m?.created_at),
                      edited_at: payload.edited_at || new Date().toISOString(),
                      reactions:
                        payload.reactions != null
                          ? normalizeMessageReactions(payload.reactions, currentUserId)
                          : m.reactions || [],
                    }
                  : m
              )
            );
          } else {
            setItems((prev) =>
              prev.map((m) =>
                m?.id === editingId ? { ...m, text, edited_at: new Date().toISOString() } : m
              )
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Edit endpoint not available yet (or failed)", err);
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message:
                  err?.response?.data?.error ||
                  t("chats.event_chat.ctx.edit_failed", {
                    defaultValue: "Could not edit this message.",
                  }),
                status: err?.response?.status,
              },
            })
          );
          return;
        }

        setEditingId(null);
        setInputValue("");
        setEditingMeta(null);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "";
            inputRef.current.style.overflowY = "auto";
          }
        });
        return;
      }

      const formData = new FormData();
      formData.append("text", text || "");
      if (isStickerSend) {
        formData.append("payload", JSON.stringify({ kind: "sticker", sticker_id: stickerId }));
      }
      if (optimisticClientId) formData.append("client_id", optimisticClientId);
      if (replyTo?.id) formData.append("reply_to", String(replyTo.id));
      if (eventContextId > 0) formData.append("event_id", String(eventContextId));
      if (startingDirectChat) {
        formData.append("send_first_message", "1");
        if (peer?.id) formData.append("peer_id", String(peer.id));
        else formData.append("code", directCode);
      }

      if (!isStickerSend && pendingImages.length) {
        pendingImages.slice(0, MAX_ATTACHMENTS).forEach((p) => {
          if (p?.file) formData.append("files", p.file);
        });
      }

      // Mark before posting because our websocket echo may win the race with
      // the HTTP response and add the message first.
      justSentRef.current = true;
      let resp;
      if (startingDirectChat) {
        const firstMessage = await sendFirstDirectChatMessage(api, formData);
        resp = firstMessage.response;
        baseUrl = firstMessage.baseUrl;
        messagesBaseUrlRef.current = baseUrl;
        deferredDirectChatId = firstMessage.chatId;
      } else {
        resp = await api.post(`${baseUrl}/messages/send/`, formData, {
          suppressGlobalError: true,
        });
      }
      sendSucceeded = true;
      const payload = resp?.data?.message;

      if (payload) {
        const rawAtt = Array.isArray(payload.attachments) ? payload.attachments : [];
        const attachments = rawAtt
          .map((a) => {
            const t = a?.type || a?.attachment_type || null;
            const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
            if (!t || !url) return null;
            return {
              type: t,
              url,
              width: a?.width ?? null,
              height: a?.height ?? null,
              original_name: a?.original_name ?? null,
              size_bytes: a?.size_bytes ?? null,
            };
          })
          .filter(Boolean);

        const newItem = {
          type: ITEM_TYPES.MESSAGE,
          created_at: payload.created_at || new Date().toISOString(),
          id: payload.id,
          author_id: payload.author_id ?? payload.author?.id ?? null,
          message_type: payload.message_type || "text",
          text: payload.text,
          payload: payload.payload || null,
          time: formatShortTime(payload.created_at),
          role: payload.author_role || null,
          author_is_deleted_account: !!payload.author_is_deleted_account,
          is_self: true,
          sender_username: payload.author_username || "@me",
          sender_code: payload.author_code || null,
          reply_to_id: replyTo?.id ? String(replyTo.id) : null,
          reply_to: replyTo
            ? { id: replyTo.id, text: replyTo.preview, author_username: replyTo.author }
            : null,
          attachments,
          reactions: normalizeMessageReactions(payload.reactions, currentUserId),
          read_by_peer:
            Number(payload.id || 0) > 0 &&
            Number(payload.id || 0) <= Number(chatMeta?.peer_last_read_message_id || 0),
        };

        setItems((prev) => {
          if (optimisticClientId) {
            return reconcileOptimisticChatMessage(prev, optimisticClientId, newItem);
          }
          const messageId = Number(newItem?.id || 0);
          if (!messageId) return [...prev, newItem];

          const existingIdx = prev.findIndex(
            (item) => Number(item?.id || 0) === messageId
          );
          if (existingIdx < 0) return [...prev, newItem];

          const next = [...prev];
          next[existingIdx] = { ...next[existingIdx], ...newItem };
          return next;
        });

        setChatMeta((prev) => ({
          ...prev,
          last_message_id: Math.max(Number(prev.last_message_id || 0), Number(payload.id || 0)),
        }));

        justSentRef.current = true;
      }

      if (eventContextId > 0 && !hasPersistedEventContextMessage) {
        setLocalEventContextActivated(true);
      }

      setReplyTo(null);
      if (!isStickerSend) {
        setInputValue("");
        clearChatDraft();
        // Preserve photos selected while this request was still in flight; they
        // belong to the next message, not the one that just completed.
        clearPendingImages(pendingImageIdsForSend);
      }

      if (deferredDirectChatId) {
        preserveOptimisticOnEmptyLoadRef.current = `/chats/direct/${encodeURIComponent(
          String(deferredDirectChatId)
        )}`;
        setResolvedDirectChatId(deferredDirectChatId);
      }

      if (!isStickerSend) {
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "";
            inputRef.current.style.overflowY = "auto";
          }
        });
      }
    } catch (err) {
      justSentRef.current = false;
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const code = String(data?.code || "").trim();

      if (code === "photo_required") {
        setHasCustomAvatar(false);
        openPhotoModal("send");
        return;
      }

      if (code === "photo_consent_required") {
        setPhotoSharingState(normalizePhotoSharingState(data?.photo_sharing));
        setPhotoConsentModalOpen(true);
        return;
      }

      // Backend may forbid sending if peer blocked me in this DM.
      if (status === 403 && data?.blocked) {
        setIsBlocked(true);
        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message:
                  data?.error ||
                  t("chats.direct_chat.blocked_send"),
                status,
              },
            })
          );
        } catch {
          // ignore
        }
        return;
      }

      if (status === 403) {
        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message:
                  data?.error ||
                  t("chats.direct_chat.send_forbidden"),
                status,
              },
            })
          );
        } catch {
          // ignore
        }
        return;
      }

      if (status === 402) {
        const quoteFromError = data?.pricing;
        if (quoteFromError) setPreStartQuote(quoteFromError);
        try {
          const res = await api.get("/miniapp/action-pricing/", {
            params: { action: "direct_chat_start" },
          });
          setPreStartQuote(res?.data || null);
        } catch {
          // ignore
        }
        window.dispatchEvent(new CustomEvent("global-error", {
          detail: { message: data?.error || t("chats.direct_chat.send_forbidden"), status },
        }));
        return;
      }

      if (status === 400) {
        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message:
                  data?.error ||
                  "Message was not sent. Please check image type/size and try again.",
                status,
              },
            })
          );
        } catch {
          // ignore
        }
        return;
      }

      if (status === 413) {
        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message: t("chats.errors.file_too_large", { max_mb: CHAT_MAX_FILE_MB }),
                status,
              },
            })
          );
        } catch {
          // ignore
        }
        return;
      }

      if (data?.error || data?.detail) {
        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message: data?.error || data?.detail,
                status: status ?? 0,
              },
            })
          );
        } catch {
          // ignore
        }
        return;
      }

      // eslint-disable-next-line no-console
      console.error("Failed to send direct chat message", err);
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message: t("chats.direct_chat.send_failed", {
            defaultValue: "Message was not sent. Please try again.",
          }),
          status: status ?? 0,
        },
      }));
    } finally {
      if (draftClearedForSend && !sendSucceeded) {
        restoreChatDraft(draftBeforeSend);
      }
      if (optimisticClientId && !sendSucceeded) {
        setItems((prev) => removeOptimisticChatMessage(prev, optimisticClientId));
      }
      if (sendSucceeded) {
        scheduleChatFeedScrollToBottom(feedRef);
      }
      sendInFlightRef.current = false;
      setSending(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBackClick = () => {
    closeCtxMenu();
    closeImageViewer();
    sendTypingState(false);
    closeChatInputSlot();

    try {
      const lastId = Number(chatMeta?.last_message_id || 0);
      if (lastId > 0) postReadUpTo(lastId);
    } catch {}

    navigateBackToChatsList();
  };

  const desktopInputBar = (
    <div className="relative w-full" style={{ "--bottom-nav-height": "0px" }}>
      <ChatInputBar {...getChatInputSlotProps()} embedded />
    </div>
  );

  const photoModal = (
    <PhotoRequestModal
      visible={isPhotoModalOpen}
      onClose={() => {
        setIsPhotoModalOpen(false);
        setPhotoGateTarget(null);
      }}
      onUploaded={() => {
        setHasCustomAvatar(true);
        setIsPhotoModalOpen(false);
        const nextAction = photoGateTarget;
        setPhotoGateTarget(null);
        if (nextAction === "send") {
          void handleSend();
        }
      }}
    />
  );

  const viewProps = {
    inputBar: isDesktop ? desktopInputBar : null,
    // i18n
    t,

    // header
    headerRef,
    handleBackClick,

    // peer
    peer: visiblePeer,
    directCode,
    openPeerVault,

    // report/block capsule + modal
    isBlocked,
    handleReportClick,
    handleManageClick,
    canManageChat: Boolean(resolvedDirectChatId),
    directLastSeenLabel,
    typingUsers,
    viewportStyle: directChatViewportStyle,
    notificationsMuted: chatNotificationsMuted,
    notificationsMutedAriaLabel: t("chats.notifications.muted_aria"),
    canToggleNotifications: canToggleChatNotifications,
    notificationsActionLabel: chatNotificationsMuted
      ? t("chats.notifications.enable_aria")
      : t("chats.notifications.disable_aria"),
    notificationsActionDisabled: chatNotificationsSubmitting,
    onNotificationsAction: openChatNotificationsModal,
    blockModalOpen,
    reportModalMode,
    closeBlockModal,
    reportCategory,
    setReportCategory,
    reportComment,
    setReportComment,
    submitBlock,
    blockSubmitting,
    safetyNoticeText: showSafetyNotice ? t("chats.direct_chat.safety_notice") : "",
    manageModalOpen,
    closeManageModal,
    onDeleteChat: () => submitManageAction("delete"),
    onDeleteAndBlockChat: () => submitManageAction("delete_and_block"),
    manageSubmitting,

    // feed
    feedRef,
    feedContentRef,
    currentUserId,
    allowSystemMessageActions: currentUserIsSuperuser,
    onFeedScroll,
    feedBottomPadPx,
    pinSparseFeed: pinSparseMobileFeed,
    items: renderedItems,
    pinnedMessages: getChatPinnedMessages(chatMeta),
    ctxMenu,
    imageViewer,
    openCtxMenu,
    flashId,
    jumpToMessage,
    openImageViewer,
    onOpenProfileByCode: openProfileByCode,
    mentionContext: resolvedDirectChatId != null ? { directChatId: resolvedDirectChatId } : null,
    directInlineReportMessageId,
    onDirectInlineReportClick: handleInlineMessageReportClick,
    directInlineReportLabel: t("chats.direct_chat.inline_report_button"),
    directInlineReportAria: t("chats.direct_chat.inline_report_aria"),
    directInlineReportDisabled: isBlocked || blockSubmitting || manageSubmitting,
    onDirectPhotoConsentAction: (action, requestId) =>
      submitPhotoConsentAction(action, requestId),
    directPhotoConsentSubmitting: photoConsentSubmitting,
    preStartNotice:
      !resolvedDirectChatId && !eventContextId && preStartQuote
        ? {
            cost: Number(preStartQuote?.cost || 0),
            balance: Number(preStartQuote?.coins_balance || 0),
            isFree: !!preStartQuote?.is_free,
          }
        : null,

    // overlay actions
    closeCtxMenu,
    copyMessage,
    beginReply,
    beginEdit,
    deleteMessage,
    onTogglePin: togglePinnedMessage,
    onToggleReaction: toggleReaction,
    canEdit,
    canDelete,
    getHeaderBottomPx,
    getBottomNavHeightPx,
    getInputBarHeightPx,
    closeImageViewer,

    // seen panel
    seenPanel,
    toggleSeenPanel,
  };

  return (
    <>
      {isDesktop ? (
        <DirectChatDesktopView {...viewProps} />
      ) : (
        <DirectChatMobileView {...viewProps} />
      )}
      <ClubChatNotificationsModal
        visible={chatNotificationsModalOpen}
        muted={chatNotificationsMuted}
        submitting={chatNotificationsSubmitting}
        onClose={closeChatNotificationsModal}
        onConfirm={submitChatNotificationsToggle}
      />
      {photoModal}
      <DirectChatPhotoConsentModal
        open={photoConsentModalOpen}
        state={photoSharingState}
        submitting={photoConsentSubmitting}
        onClose={() => {
          if (!photoConsentSubmitting) setPhotoConsentModalOpen(false);
        }}
        onRequest={() => submitPhotoConsentAction("request")}
        onAccept={() =>
          submitPhotoConsentAction(
            "accept",
            photoSharingState?.incoming_request?.id,
            { openPickerAfterAccept: true }
          )
        }
        onDecline={() =>
          submitPhotoConsentAction(
            "decline",
            photoSharingState?.incoming_request?.id
          )
        }
        t={t}
      />
    </>
  );
}
